import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Button,
  Checkbox,
  Center,
  Flex,
  useToast,
  Text,
  Spinner,
  Select,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  MenuDivider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Td,
  Badge,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Heading,
  Textarea,
  UnorderedList,
  ListItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileAudio,
  faFolder,
  faFile,
  faArrowUp,
  faArrowDown,
  faShare,
  faUserMinus,
  faMicrophone,
} from "@fortawesome/free-solid-svg-icons";
import "../css/SchedulesTable.css";
import { getSupabase, supabaseSchema } from "./supabaseClient";
import { shadowCompareScheduleList } from "../utils/rdsShadowRead";
import { dbForRow, foreignSchemaOf, storageSchemaOf } from "../utils/rowSchema";
import { CANONICAL_TYPES } from "../utils/scheduleImport/normalizeType";
import { deleteFilesWithCacheCleanup } from "../utils/storageUtils";
import { convertScheduledTime } from "../utils/scheduleTimeDisplay.js";
import {
  generateMedADocsPath,
  deriveSimpleScheduleMedADocsPath,
  deriveLongScheduleMedADocsPath,
  resolveDocumentsTableIdByPathFirstMatch,
} from "../documentComponents/documentUtilities";
import { useLocation, useNavigate } from "react-router-dom";
import AudioPlayerModal from "./table/audio/AudioPlayerModal";
import LateBy from "./LateBy";
import ResultsTable from "./table/ResultsTable";
import {
  convertDate,
  copyItems,
  deleteItems,
  dupByCol,
  fetchNoShows,
  handleViewAudioFiles,
  sortObjectsByTime,
  getLiveTranscriptionSetting,
  getGlobalLiveTranscriptionSetting,
} from "../utils/utils";
import { hasValidPassword } from "../utils/passwordProtection";
import { getPatientPageVisitPathSegment } from "../utils/visitPathUtils";
import { formatAiRecapBullet } from "../utils/aiRecapDisplay";
import {
  filterAudiosForVisitDate,
  filterTranscriptsForVisitDate,
  transcriptShowsAsLive,
} from "../utils/audioMetadata";
import PasswordModal from "./PasswordModal";
import JSONToFormattedText from "./JSONToMarkdown";
import { CSVLink } from "react-csv";
import AddPatientModal from "./AddPatientModal";
import { checkCreditLimitAndUpdateUI } from "../utils/creditLimitChecker";
import ReactModal from "react-modal";
import FormsModal from "./FormsModal";
import PatientMessagingModal from "./PatientMessagingModal";
import ShareRowModal, {
  SHARE_ROLE_SELECT_OPTIONS,
} from "./ShareRowModal";
import {
  mergeSharedUsersAdd,
  mergeSharedUsersRemove,
  mergeShareRoleMaps,
  normalizeShareRole,
  parseOwnerShareAllEntries,
  parseSharedUserEntry,
  pickMoreRestrictiveShareRole,
  roleMapFromSharedUsers,
  serializeOwnerShareAllEntries,
  resolveTenantRowAccess,
  sharedUsersOverlapOrClauses,
  sharedUsersHasViewer,
  SHARE_ROLES,
} from "../utils/shareAccess";
import { extractAllSubmissionLinks } from "../utils/submissionUtils";
import activeDocumentTracker from "../utils/activeDocumentTracker";
import {
  normalizeRole,
  isInGroup,
  isPatientRole,
  filterRowsForPatient,
  hasClinicalAdminPermissions,
  canAccessPage,
  getLabel,
  getScheduleField,
} from "../config/roles";
import { fetchEffectiveUserRow } from "../utils/effectiveUserRole";
import ScheduleImportModal from "./ScheduleImportModal";
import { hydrateScheduleRowsTimezoneSource } from "../utils/scheduleRowTimezone.js";
import { getOwnerIndex } from "../utils/scheduleOwners";
import {
  bucketName_files,
  tableName,
  getDefaultDateString,
  prompts,
  rangeInMinutes,
  colid,
  refreshTimer,
  supabaseUrl,
  defaultItemsPerPage,
  BASE_URLS,
  noShowTableName,
  displayOrder,
  activeDisplayOrder,
  basename,
  getBackendURL,
} from "./env";
import { buildReportsFolder } from "../utils/reportsPath";
import { scheduleFoldersWithFiles } from "../utils/reportsFolderFiles";
import { fetchConfirmationSchedule } from "../utils/confirmationScheduling";
import {
  prepareFormProcessingData,
} from "../utils/formProcessingUtils";
import { useReportProcessing } from "../hooks/useReportProcessing";
import ProcessFormPromptEditor from "./ProcessFormPromptEditor";
import ProcessReportPromptEditor from "./ProcessReportPromptEditor";
import { useExternalRecording } from "../pages/AudioRecorder/hooks/useExternalRecording";
import { UserListPicker, UserListManagerModal } from '../userLists';
import { useSelectedProviders } from '../hooks/useSelectedProviders';

// Suppress React DOM nesting warnings and isDisabled prop warnings
// eslint-disable-next-line import/first
const originalConsoleError = console.error;
console.error = (...args) => {
  const message = args[0];
  if (
    typeof message === "string" &&
    (message.includes("validateDOMNesting") ||
      message.includes("does not recognize the `isDisabled` prop"))
  ) {
    return; // Suppress these specific warnings
  }
  originalConsoleError.apply(console, args);
};

// The four clinical codes are opaque on their own, so the type filter spells
// them out. A type that isn't one of these (a free-text value an import left
// alone) falls through and is shown raw — including the non-clinical roles,
// where `type` means "Class Type" / "Meeting Type" and carries its own words.
const TYPE_LABELS = {
  NP: "New · Pulmonary",
  NS: "New · Sleep",
  FFP: "Follow-up · Pulmonary",
  FFS: "Follow-up · Sleep",
};

// Flatten per-schema fetch results, tagging each row with its source schema.
// A schema whose fetch failed contributes `rows: null` and is skipped.
export function mergeSchemaResults(results) {
  return results
    .filter((r) => Array.isArray(r.rows))
    .flatMap((r) => r.rows.map((row) => ({ ...row, _schema: r.schema })));
}

const SchedulesTable = ({
  showCheckboxes = true,
  setSchedules,
  onSelectedRowChange,
  isAdminPage = false,
  processingFiles = {}, // Track files being processed: { filePath: { status: 'uploading'|'classifying'|'extracting', message: string } }
  externalSelectedRows = null, // Accept external selectedRows from parent
  smsCache = null, // Pre-fetched SMS cache from AdminPage
  userRole: parentUserRole = null, // User role from parent for filtering columns
  refetchSchedules = null, // Optional callback to refetch schedules (e.g. after share all)
  actionsRef = null, // Parent populates with imperative actions (e.g. removeSchedulesById)
  // (rowId, fields) after this table patches a row in place (today: a
  // Confirm-status change and its rollback), so a parent rendering the same
  // rows elsewhere (the Schedules simple view) stays in step. Opt-in: the
  // Admin pages leave it unset, so their row state isn't touched.
  onRowPatched = null,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { selected: selectedProviders, homeSchema } = useSelectedProviders();
  // Stable primitive derived from the selected-providers array, so the fetch
  // effect below can depend on "did the provider selection actually change"
  // without depending on array identity (a new array reference from the hook
  // on every render would otherwise refetch on every render). For non-clinical
  // users (and clinical users who never touch the dropdown) this is always
  // the same single-schema string, so it never causes an extra refetch.
  const providersKey = (selectedProviders || []).join(',');

  // State declarations
  const [date, setDate] = useState(null);
  const [allSchedules, setAllSchedules] = useState([]);
  const [schedules, setLocalSchedules] = useState([]);
  const [refreshData, setRefreshData] = useState(false);
  const [buttonTexts, setButtonTexts] = useState([]);
  const [defaultButtonTexts, setDefaultButtonTexts] = useState([]);
  const [selectedRows, setSelectedRows] = useState({});

  // --- Display filters ----------------------------------------------------
  // Both narrow what the table renders; neither refetches. The provider filter
  // is a *view* narrowing inside what ProvidersDropdown already loaded, so it
  // only offers itself once rows from more than one schema are actually
  // present. Empty array == "no filter", which is the default, so a user who
  // never opens these menus sees exactly what they saw before.
  const [typeFilter, setTypeFilter] = useState([]);
  const [schemaFilter, setSchemaFilter] = useState([]);

  const normalizedTypeOf = (row) =>
    String(row?.type || "").trim().toUpperCase();

  const availableTypes = useMemo(() => {
    const seen = new Set();
    schedules.forEach((r) => {
      const t = normalizedTypeOf(r);
      if (t) seen.add(t);
    });
    // Canonical codes first in their documented NP/NS/FFP/FFS order, then
    // anything else an import left behind — normalizeAppointmentType returns
    // an unrecognised phrase untouched rather than guessing, so free-text
    // types are expected here and must stay filterable.
    const extras = [...seen].filter((t) => !CANONICAL_TYPES.includes(t)).sort();
    return [...CANONICAL_TYPES.filter((t) => seen.has(t)), ...extras];
  }, [schedules]);

  const availableSchemas = useMemo(() => {
    const seen = new Set();
    schedules.forEach((r) => {
      if (r?._schema) seen.add(r._schema);
    });
    return [...seen].sort();
  }, [schedules]);

  const visibleSchedules = useMemo(() => {
    if (!typeFilter.length && !schemaFilter.length) return schedules;
    return schedules.filter((r) => {
      if (typeFilter.length && !typeFilter.includes(normalizedTypeOf(r)))
        return false;
      if (schemaFilter.length && !schemaFilter.includes(r?._schema))
        return false;
      return true;
    });
  }, [schedules, typeFilter, schemaFilter]);

  const filtersActive = typeFilter.length > 0 || schemaFilter.length > 0;

  // Counts shown beside each menu option. Deliberately computed over the full
  // `schedules` list, not the filtered one, so a count never drops to 0 just
  // because the *other* menu is narrowing the view.
  const typeCounts = useMemo(() => {
    const counts = {};
    schedules.forEach((r) => {
      const t = normalizedTypeOf(r);
      if (t) counts[t] = (counts[t] || 0) + 1;
    });
    return counts;
  }, [schedules]);

  const schemaCounts = useMemo(() => {
    const counts = {};
    schedules.forEach((r) => {
      if (r?._schema) counts[r._schema] = (counts[r._schema] || 0) + 1;
    });
    return counts;
  }, [schedules]);

  // A filter kept from a previous day (or a deselected provider) would
  // silently hide every row, so drop choices that no longer exist. Returning
  // `prev` unchanged when nothing was pruned avoids a render on every fetch.
  useEffect(() => {
    setTypeFilter((prev) => {
      const next = prev.filter((t) => availableTypes.includes(t));
      return next.length === prev.length ? prev : next;
    });
  }, [availableTypes]);

  useEffect(() => {
    setSchemaFilter((prev) => {
      const next = prev.filter((s) => availableSchemas.includes(s));
      return next.length === prev.length ? prev : next;
    });
  }, [availableSchemas]);

  // Selecting rows, then filtering them out of view, would leave "Remove N
  // selected" acting on rows the user can no longer see. Prune the selection
  // to what's visible whenever the filters change.
  useEffect(() => {
    if (!filtersActive) return;
    const visibleIds = new Set(visibleSchedules.map((r) => String(r.id)));
    setSelectedRows((prev) => {
      const next = {};
      Object.keys(prev).forEach((id) => {
        if (prev[id] && visibleIds.has(String(id))) next[id] = prev[id];
      });
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [visibleSchedules, filtersActive]);

  const [userRole, setUserRole] = useState(null);
  const [viewerTimezone, setViewerTimezone] = useState("America/New_York");
  const [selectedDate, setSelectedDate] = useState(
    new URLSearchParams(location.search).get("date")
      ? new URLSearchParams(location.search).get("date")
      : getDefaultDateString(),
  );
  const [selectedTime, setSelectedTime] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
  const [totalPages, setTotalPages] = useState(1);
  const [currentFolderPath, setCurrentFolderPath] = useState("");
  const [previousFolderPath, setPreviousFolderPath] = useState("");
  const [currentFolderFiles, setCurrentFolderFiles] = useState([]);
  const [isAudioPlayerModalOpen, setIsAudioPlayerModalOpen] = useState(false);
  const [audio, setAudio] = useState(undefined);
  const [allAudios, setAllAudios] = useState([]);
  const [audioTranscripts, setAudioTranscripts] = useState([]);
  const [scheId, setScheId] = useState(undefined);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [isInTimeRange, setIsInTimeRange] = useState([]);
  const [dups, setDups] = useState([]);
  const [showTd, setShowTd] = useState([]);
  const [noShowsTd, setNoShowsTd] = useState([]);
  const [noShows, setNoShows] = useState([]);
  const [currentView, setCurrentView] = useState("default");
  const [isAddPatientModalOpen, setIsAddPatientModalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isViewSavedResultsModalOpen, setIsViewSavedResultsModalOpen] =
    useState(false);

  // Determine which columns to hide for freemium users
  // Use parentUserRole if available, otherwise use local userRole state
  const effectiveUserRole = parentUserRole || userRole;
  const patientView = isPatientRole(effectiveUserRole);
  // Per-date automatic-confirmation grading for the Status badge ("no auto"
  // marker). Loaded alongside the schedules; empty means "no marker".
  const [confirmationAutoStatus, setConfirmationAutoStatus] = useState({});
  const [confirmationWindowDays, setConfirmationWindowDays] = useState(null);
  const loadConfirmationAutoStatus = async () => {
    if (patientView) return;
    const isValidDate = selectedDate && !isNaN(new Date(selectedDate).getTime());
    const date = isValidDate ? new Date(selectedDate).toISOString().split("T")[0] : null;
    // Rows can be fanned in from every selected provider schema (see the
    // schedule fetch above), so the auto-confirmation status has to be
    // pulled per schema too — querying only `supabaseSchema` (the viewer's
    // home schema) left a foreign-schema row's "no auto" marker permanently
    // missing (the home schema's map has no entry for its id) and, worse, a
    // same-numbered home-schema id would show that row's status instead.
    // Namespace keys by schema so ids that collide across schemas never
    // shadow each other.
    const schemas =
      selectedProviders && selectedProviders.length
        ? selectedProviders
        : [supabaseSchema];
    const results = await Promise.all(
      schemas.map((schema) => fetchConfirmationSchedule({ schema, date })),
    );
    const mergedStatus = {};
    let windowDays = null;
    results.forEach((result, i) => {
      const schema = schemas[i];
      const map = result?.autoStatus || {};
      Object.keys(map).forEach((id) => {
        mergedStatus[`${schema}::${id}`] = map[id];
      });
      if (windowDays === null && result?.windowDays != null) {
        windowDays = result.windowDays;
      }
    });
    setConfirmationAutoStatus(mergedStatus);
    setConfirmationWindowDays(windowDays);
  };
  const hiddenColumns = React.useMemo(() => {
    console.log(
      "[SchedulesTable] Computing hidden columns for role:",
      effectiveUserRole,
    );
    const hidden = getScheduleField(effectiveUserRole, 'hiddenColumns');
    console.log("[SchedulesTable] Hidden columns:", hidden);
    if (hidden.length === 0) {
      console.log(
        "[SchedulesTable] No columns hidden - user is not a freemium user or role is:",
        effectiveUserRole,
      );
    } else {
      console.log(
        "[SchedulesTable] Columns being hidden because user role is:",
        effectiveUserRole,
      );
    }
    return hidden;
  }, [effectiveUserRole]);
  const [savedTranscriptions, setSavedTranscriptions] = useState([]);
  const [isViewNotesModalOpen, setIsViewNotesModalOpen] = useState(false);
  const [selectedTranscription, setSelectedTranscription] = useState(null);
  const [generatingAiSummaryId, setGeneratingAiSummaryId] = useState(null);
  const [selectedPatientForSavedResults, setSelectedPatientForSavedResults] =
    useState(null);
  const [savedTranscriptionsCount, setSavedTranscriptionsCount] = useState({});

  // Password modal state
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  // Active document tracking state
  const [activeDocuments, setActiveDocuments] = useState([]);
  const [activePatients, setActivePatients] = useState(new Set());

  // State to track which schedules have files in their reports folder
  const [schedulesWithFiles, setSchedulesWithFiles] = useState(new Set());
  const [filesCheckComplete, setFilesCheckComplete] = useState(false);

  // Forms modal state - using shared FormsModal component
  const [isFormsModalOpen, setIsFormsModalOpen] = useState(false);
  const [selectedPatientIdForForms, setSelectedPatientIdForForms] =
    useState(null);
  // Carries the row's source schema so cross-schema (linked-provider) patients
  // resolve submissions_array / StoringFormSubmission reads against the right schema.
  const [selectedPatientDataForForms, setSelectedPatientDataForForms] =
    useState(null);

  // Process form prompt editor modal state
  const [isPromptEditorOpen, setIsPromptEditorOpen] = useState(false);
  const [currentPatientType, setCurrentPatientType] = useState("");
  const [promptEditorInitialData, setPromptEditorInitialData] = useState(null);
  const [
    selectedPatientDataForProcessing,
    setSelectedPatientDataForProcessing,
  ] = useState(null);

  // Process report prompt editor modal state
  const {
    isReportPromptEditorOpen,
    setIsReportPromptEditorOpen,
    currentReportType,
    reportPromptEditorInitialData,
    handleProcessReport,
    handleReportProcessingRun,
  } = useReportProcessing({
    onRun: ({ fullPrompt, currentReportFullText, currentReportFileNames }) => {
      if (!currentViewingPatient?.id) {
        toast({
          title: "Error",
          description: "No patient data available",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      const visitSeg = getPatientPageVisitPathSegment(currentViewingPatient);
      const search = `?date=${selectedDate}&openProcessReport=1`;
      const reportRoutePrefix = canAccessPage(effectiveUserRole, 'clinicPage') ? 'clinic' : 'visit';
      const url = `${window.location.origin}${basename || ""}/${reportRoutePrefix}/${visitSeg}${search}`;
      try {
        localStorage.setItem(
          "notesai_processReport_payload",
          JSON.stringify({
            prefillReportPrompt: fullPrompt,
            prefillReportFullText: currentReportFullText || null,
            prefillReportFileNames: currentReportFileNames || null,
          }),
        );
        window.open(url, "_blank", "noopener,noreferrer");
        toast({
          title: "Opened in new tab",
          description: "AI chat opened in a new tab. This page stays here.",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
      } catch (e) {
        console.error("Failed to open process report in new tab:", e);
        navigate(`/${reportRoutePrefix}/${visitSeg}?date=${selectedDate}`, {
          state: {
            openProcessReport: true,
            prefillReportPrompt: fullPrompt,
            prefillReportFullText: currentReportFullText,
            prefillReportFileNames: currentReportFileNames,
          },
        });
      }
    },
    toast,
  });
  const [currentViewingPatient, setCurrentViewingPatient] = useState(null);

  // Messaging modal state - only used in AdminPage
  const [isMessagingModalOpen, setIsMessagingModalOpen] = useState(false);
  const [selectedPatientForMessaging, setSelectedPatientForMessaging] =
    useState(null);
  const [selectedPatientUnreadCount, setSelectedPatientUnreadCount] =
    useState(0);
  // Trigger to refresh unread messages count in ResultsTable after modal closes
  const [refreshUnreadMessagesTrigger, setRefreshUnreadMessagesTrigger] =
    useState(0);

  // Share schedule with other users (tenant roles: student, socialworker, other)
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareSchedule, setShareSchedule] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserName, setCurrentUserName] = useState(null);
  // Signed-in email, lifted out of getSchedules' local scope so the manual
  // Confirm-status override can stamp confirmation_updated_by (the audit
  // column) with a real editor instead of "".
  const [currentUserEmail, setCurrentUserEmail] = useState("");
  const [shareModalUsers, setShareModalUsers] = useState([]);
  const [shareModalLoading, setShareModalLoading] = useState(false);
  const [shareModalAlreadySharedIds, setShareModalAlreadySharedIds] = useState(
    new Set(),
  );
  const [shareModalSharedUserRoles, setShareModalSharedUserRoles] = useState(
    {},
  );
  const [shareModalUpdatingRoleId, setShareModalUpdatingRoleId] =
    useState(null);
  const [tenantShareAllOwnerIds, setTenantShareAllOwnerIds] = useState([]);
  const [tenantShareAllOwnerRoles, setTenantShareAllOwnerRoles] = useState({});

  // Import a day's appointments from a CSV / Excel export
  const [importModalOpen, setImportModalOpen] = useState(false);

  // Share all rows (past and future) with selected users
  const [shareAllModalOpen, setShareAllModalOpen] = useState(false);
  const [shareAllUsersList, setShareAllUsersList] = useState([]);
  const [shareAllSelectedIds, setShareAllSelectedIds] = useState(new Set());
  const [shareAllRecipientEntries, setShareAllRecipientEntries] = useState(
    [],
  );
  const [shareAllDefaultRole, setShareAllDefaultRole] = useState(
    SHARE_ROLES.ADMIN,
  );
  const [shareAllLoading, setShareAllLoading] = useState(false);
  const [shareAllSaving, setShareAllSaving] = useState(false);
  const [shareAllManagerOpen, setShareAllManagerOpen] = useState(false);
  // Map<userId, role> populated by the user-list picker so per-user role
  // overrides set inside the picker survive when handleShareAllRows writes
  // out the share-all entries. Falls back to shareAllDefaultRole otherwise.
  const [shareAllRoleByUserId, setShareAllRoleByUserId] = useState({});
  const [shareAllRevokingId, setShareAllRevokingId] = useState(null);
  const [shareAllBadgeCount, setShareAllBadgeCount] = useState(0);
  const [pendingRecordingRow, setPendingRecordingRow] = useState(null);
  const externalRecordingWindowRef = useRef(null);

  const pendingRecordingPatientId = pendingRecordingRow?.id || null;
  const pendingRecordingPatientName =
    pendingRecordingRow?.[prompts[0][0]] ||
    pendingRecordingRow?.patient_name ||
    "Patient";
  const pendingRecordingEhrId =
    pendingRecordingRow?.EHR_ID || pendingRecordingRow?.ehr_id || null;

  const { generateExternalHTML, getDynamicWindowSize } = useExternalRecording({
    state: pendingRecordingRow
      ? {
          patient_id: pendingRecordingPatientId,
          date_:
            pendingRecordingRow?.date_ ||
            new Date().toISOString().split("T")[0],
          // Rows merged in from a linked clinic live in THEIR schema; without
          // the tag the recorder aimed at the viewer's home schema instead.
          _schema: pendingRecordingRow?._schema,
        }
      : {},
    refId: pendingRecordingPatientId,
    patientId: pendingRecordingPatientId,
    patientName: pendingRecordingPatientName,
    ehrId: pendingRecordingEhrId,
    liveTranscription: true,
    userRole: effectiveUserRole,
    aiRecapDefault: true,
  });

  // The latest Confirm-status handler (defined further down, and re-created
  // every render) — read through a ref so the imperative action below never
  // runs a stale copy.
  const setConfirmationStatusRef = useRef(null);
  // Same for opening a row's View Reports window (handleViewReports, further down).
  const openReportsRef = useRef(null);

  // Expose imperative actions to the parent so it can drop rows from the
  // local table state without forcing a refetch (e.g. after Mark-as-No-Show).
  useEffect(() => {
    if (!actionsRef) return;
    actionsRef.current = {
      removeSchedulesById: (idsToRemove) => {
        const idSet = new Set(idsToRemove);
        setLocalSchedules((prev) => prev.filter((s) => !idSet.has(s.id)));
        setAllSchedules((prev) => prev.filter((s) => !idSet.has(s.id)));
      },
      // The simple view's Confirm chip: same write, audit stamp and rollback
      // as the Confirm column. Resolves true once the backend has saved it.
      setConfirmationStatus: (rowId, status) =>
        setConfirmationStatusRef.current
          ? setConfirmationStatusRef.current(rowId, status)
          : Promise.resolve(false),
      // The row's View Reports window, as its View Reports button opens it
      // (the Admin page's Patients menu).
      openReports: (row) => openReportsRef.current?.(row),
    };
    return () => {
      if (actionsRef.current) actionsRef.current = null;
    };
  }, [actionsRef]);

  useEffect(() => {
    if (!pendingRecordingRow) return;

    if (
      externalRecordingWindowRef.current &&
      !externalRecordingWindowRef.current.closed
    ) {
      externalRecordingWindowRef.current.focus();
      setPendingRecordingRow(null);
      return;
    }

    try {
      const htmlContent = generateExternalHTML();
      const { width, height } = getDynamicWindowSize();
      const popup = window.open(
        "",
        "externalRecording",
        `width=${width},height=${height},scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no,titlebar=yes,top=0,left=0`,
      );

      if (!popup) {
        toast({
          title: "Popup blocked",
          description:
            "Please allow popups for this site to open the external recorder.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
        setPendingRecordingRow(null);
        return;
      }

      popup.document.write(htmlContent);
      popup.document.close();
      externalRecordingWindowRef.current = popup;
      setPendingRecordingRow(null);

      const closeWatcher = setInterval(() => {
        if (popup.closed) {
          clearInterval(closeWatcher);
          externalRecordingWindowRef.current = null;
        }
      }, 500);
    } catch (error) {
      console.error("Failed to open external recording window:", error);
      toast({
        title: "Recording window error",
        description: "Could not open the external recorder. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setPendingRecordingRow(null);
    }
  }, [pendingRecordingRow, generateExternalHTML, getDynamicWindowSize, toast]);
  const [shareAllUpdatingRoleId, setShareAllUpdatingRoleId] = useState(null);
  const [shareAllSearchQuery, setShareAllSearchQuery] = useState("");
  const [shareModalRemovingId, setShareModalRemovingId] = useState(null);

  /** Can the viewer delete this specific row? Tenant viewer/editor/shared
   * read+write cannot. Used per row so a multi-row selection can remove the
   * rows it is allowed to and report the rest, rather than all-or-nothing. */
  const canDeleteRowId = React.useCallback((id) => {
    if (patientView) return false;
    if (!isInGroup(effectiveUserRole, 'tenantSchema') || !currentUserId) return true;
    const row = schedules.find((s) => String(s.id) === String(id));
    if (!row) return true;
    const acc = resolveTenantRowAccess({
      viewerId: currentUserId,
      rowUserId: row.user_id,
      sharedUsers: row.shared_users,
      shareAllOwnerIds: tenantShareAllOwnerIds,
      shareAllOwnerRoles: tenantShareAllOwnerRoles,
    });
    return acc.canDeleteRow;
  }, [
    schedules,
    currentUserId,
    effectiveUserRole,
    tenantShareAllOwnerIds,
    tenantShareAllOwnerRoles,
    patientView,
  ]);

  /** Remove row: disabled when nothing selected, or when NONE of the selected
   * rows may be deleted. A mixed selection stays enabled — the deletable rows
   * go and the blocked ones are named in the result toast. */
  const isRemoveRowDisabled = React.useMemo(() => {
    const ids = Object.keys(selectedRows);
    if (ids.length === 0) return true;
    if (patientView) return true;
    return !ids.some((id) => canDeleteRowId(id));
  }, [selectedRows, patientView, canDeleteRowId]);

  // Track previous processingFiles to detect when files finish processing
  const prevProcessingFilesRef = useRef({});

  // Auto-refresh folder files when a file finishes processing
  useEffect(() => {
    const prevProcessingFiles = prevProcessingFilesRef.current;
    const prevKeys = Object.keys(prevProcessingFiles);
    const currentKeys = Object.keys(processingFiles);

    // Check if any file just finished processing (was in prev but not in current)
    const finishedFiles = prevKeys.filter((key) => !currentKeys.includes(key));

    if (finishedFiles.length > 0 && modalIsOpen && currentFolderPath) {
      console.log(
        "🔄 Files finished processing, refreshing folder:",
        finishedFiles,
      );
      // Small delay to allow backend to complete updating the database
      const timeoutId = setTimeout(() => {
        // Refresh the folder files
        const refreshFiles = async () => {
          const supabase = await getSupabase();
          try {
            const { data, error } = await supabase.storage
              .from(bucketName_files)
              .list(currentFolderPath);

            if (!error && data) {
              const filesOnly = data.filter((item) => item.name.includes("."));
              setCurrentFolderFiles(filesOnly);
              console.log(
                `✅ Auto-refreshed ${filesOnly.length} files after processing complete`,
              );
            }
          } catch (error) {
            console.error("Error auto-refreshing folder files:", error);
          }
        };
        refreshFiles();
      }, 500); // Small delay to ensure backend has updated

      return () => clearTimeout(timeoutId);
    }

    // Update the ref with current processingFiles
    prevProcessingFilesRef.current = processingFiles;
  }, [processingFiles, modalIsOpen, currentFolderPath]);

  // Sync external selectedRows with internal state
  useEffect(() => {
    if (externalSelectedRows !== null) {
      setSelectedRows(externalSelectedRows);
    }
  }, [externalSelectedRows]);

  // Fetch users for Share modal (same schema) — use backend API to bypass RLS
  useEffect(() => {
    if (!shareModalOpen || !shareSchedule) {
      setShareModalUsers([]);
      setShareModalAlreadySharedIds(new Set());
      setShareModalSharedUserRoles({});
      return;
    }
    let cancelled = false;
    setShareModalLoading(true);
    const nameField = prompts[0][0];
    const fetchUsersAndAlreadyShared = async () => {
      try {
        const supabase = await getSupabase();
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const userId = session?.user?.id || currentUserId;
        if (!userId) {
          setShareModalLoading(false);
          return;
        }
        const backendUrl = getBackendURL();
        const res = await fetch(
          `${backendUrl}/api/schema-users/?user_id=${encodeURIComponent(userId)}`,
        );
        const json = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && Array.isArray(json.users)) {
          setShareModalUsers(json.users || []);
        } else {
          const { data, error } = await supabase
            .from("users")
            .select("id, email, name, role")
            .order("email");
          if (cancelled) return;
          if (error)
            console.error("[Share modal] Supabase fallback error:", error);
          setShareModalUsers(data || []);
        }
        const entityName = shareSchedule[nameField];
        const hasEntityName =
          entityName != null && String(entityName).trim() !== "";
        const alreadyIds = new Set();
        const roleMap = {};
        if (hasEntityName) {
          const { data: siblingRows } = await supabase
            .from(tableName)
            .select("shared_users")
            .eq("user_id", shareSchedule.user_id)
            .eq(nameField, entityName);
          if (cancelled) return;
          (siblingRows || []).forEach((row) => {
            mergeShareRoleMaps(roleMap, roleMapFromSharedUsers(row.shared_users));
            const arr = Array.isArray(row.shared_users) ? row.shared_users : [];
            arr.forEach((entry) => {
              const p = parseSharedUserEntry(entry);
              if (p) alreadyIds.add(p.id);
            });
          });
        } else {
          mergeShareRoleMaps(
            roleMap,
            roleMapFromSharedUsers(shareSchedule.shared_users),
          );
          const arr = Array.isArray(shareSchedule.shared_users)
            ? shareSchedule.shared_users
            : [];
          arr.forEach((entry) => {
            const p = parseSharedUserEntry(entry);
            if (p) alreadyIds.add(p.id);
          });
        }
        const { data: myRow } = await supabase
          .from("users")
          .select("shared_users")
          .eq("id", userId)
          .single();
        if (cancelled) return;
        const raw = myRow?.shared_users;
        if (raw != null) {
          const { entries } = parseOwnerShareAllEntries(raw);
          entries.forEach((e) => {
            const kid = String(e.id).toLowerCase();
            alreadyIds.add(kid);
            const r = normalizeShareRole(e.role);
            if (roleMap[kid] == null) roleMap[kid] = r;
            else roleMap[kid] = pickMoreRestrictiveShareRole(roleMap[kid], r);
          });
        }
        setShareModalSharedUserRoles(roleMap);
        setShareModalAlreadySharedIds(alreadyIds);
      } catch (err) {
        if (!cancelled) console.error("[Share modal] Fetch error:", err);
        try {
          const supabase = await getSupabase();
          const { data, error } = await supabase
            .from("users")
            .select("id, email, name, role")
            .order("email");
          if (!cancelled) {
            if (error) console.error("[Share modal] Fallback error:", error);
            setShareModalUsers(data || []);
          }
        } catch (_) {}
      } finally {
        if (!cancelled) setShareModalLoading(false);
      }
    };
    fetchUsersAndAlreadyShared();
    return () => {
      cancelled = true;
    };
  }, [
    shareModalOpen,
    shareSchedule?.id,
    shareSchedule?.user_id,
    shareSchedule?.[prompts[0][0]],
    currentUserId,
  ]);

  // Fetch users and current "share all" recipients when Share All modal opens
  useEffect(() => {
    if (!shareAllModalOpen) {
      setShareAllUsersList([]);
      setShareAllSelectedIds(new Set());
      setShareAllRoleByUserId({});
      setShareAllRecipientEntries([]);
      return;
    }
    let cancelled = false;
    setShareAllLoading(true);
    const fetchUsersAndCurrentRecipients = async () => {
      try {
        const supabase = await getSupabase();
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const userId = session?.user?.id || currentUserId;
        if (!userId) {
          console.warn(
            "[Share all modal] No user id available for schema-users request",
          );
          setShareAllLoading(false);
          return;
        }
        const backendUrl = getBackendURL();
        const url = `${backendUrl}/api/schema-users/?user_id=${encodeURIComponent(userId)}`;
        const res = await fetch(url);
        const json = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && Array.isArray(json.users)) {
          setShareAllUsersList(json.users || []);
        } else {
          const { data, error } = await supabase
            .from("users")
            .select("id, email, name, role")
            .order("email");
          if (cancelled) return;
          if (error)
            console.error("[Share all modal] Supabase fallback error:", error);
          setShareAllUsersList(data || []);
        }
        setShareAllSelectedIds(new Set());
        setShareAllRoleByUserId({});
        // Fetch current user's shared_users (who already has "share all" access)
        const { data: myRow } = await supabase
          .from("users")
          .select("shared_users")
          .eq("id", userId)
          .single();
        if (cancelled) return;
        const { entries } = parseOwnerShareAllEntries(myRow?.shared_users);
        setShareAllRecipientEntries(entries);
      } catch (err) {
        if (!cancelled) console.error("[Share all modal] Fetch error:", err);
        try {
          const supabase = await getSupabase();
          const { data, error } = await supabase
            .from("users")
            .select("id, email, name, role")
            .order("email");
          if (!cancelled) {
            if (error)
              console.error("[Share all modal] Fallback error:", error);
            setShareAllUsersList(data || []);
          }
        } catch (_) {}
      } finally {
        if (!cancelled) setShareAllLoading(false);
      }
    };
    fetchUsersAndCurrentRecipients();
    return () => {
      cancelled = true;
    };
  }, [shareAllModalOpen, currentUserId]);

  // Fetch share-all badge count whenever currentUserId is available
  useEffect(() => {
    if (!currentUserId) return;
    const fetchShareAllBadgeCount = async () => {
      const supabase = await getSupabase();
      const { data: myRow } = await supabase
        .from("users")
        .select("shared_users")
        .eq("id", currentUserId)
        .maybeSingle();
      if (myRow?.shared_users != null) {
        const { entries } = parseOwnerShareAllEntries(myRow.shared_users);
        setShareAllBadgeCount(entries.length);
      }
    };
    fetchShareAllBadgeCount();
  }, [currentUserId]);

  // Initialize active document tracking
  useEffect(() => {
    let unsubscribe = null;

    const initializeActiveDocumentTracking = async () => {
      try {
        await activeDocumentTracker.initialize();

        // Subscribe to active document updates
        unsubscribe = activeDocumentTracker.subscribe((activeDocs) => {
          setActiveDocuments(activeDocs);

          // Extract patient IDs from active documents
          const patientIds = new Set();
          activeDocs.forEach((doc) => {
            if (doc.patientId) {
              patientIds.add(doc.patientId);
            }
          });
          setActivePatients(patientIds);

          console.log("📄 Active documents updated:");
          activeDocs.forEach((doc, index) => {
            console.log(`  [${index}] Path: ${doc.path}`);
            console.log(`  [${index}] Patient ID: ${doc.patientId}`);
            console.log(`  [${index}] Patient name: ${doc.patientName}`);
            console.log(`  [${index}] Users: ${doc.users.length}`);
            doc.users.forEach((user, userIndex) => {
              console.log(`    [${userIndex}] User: ${user.name} (${user.id})`);
              console.log(
                `    [${userIndex}] Last activity: ${user.lastActivity}`,
              );
            });
            console.log(`  [${index}] Last activity: ${doc.lastActivity}`);
          });
          console.log("👥 Active patients:", Array.from(patientIds));
        });

        console.log("✅ Active document tracking initialized");
      } catch (error) {
        console.error("❌ Error initializing active document tracking:", error);
      }
    };

    initializeActiveDocumentTracking();

    return () => {
      if (unsubscribe && typeof unsubscribe === "function") {
        unsubscribe();
      }
      activeDocumentTracker.cleanup();
    };
  }, []);

  // Bumped by every fetchData run. A run that has been superseded (the
  // provider selection resolved, or the date changed, while it was in flight)
  // stops before its expensive reads and never writes state, so an older,
  // slower run cannot overwrite the newer run's rows.
  const fetchGenRef = useRef(0);

  // Fetch schedules from Supabase
  const getSchedules = async (nsPromise, gen) => {
    const superseded = () => gen !== fetchGenRef.current;
    const supabase = await getSupabase();
    setIsLoading(true);
    setFetchError(null);
    try {
      const { data: sessionData, error: sessionError } =
        await supabase.auth.getSession();
      if (superseded()) return;
      if (sessionError) {
        console.error(
          "Error getting session:",
          sessionError.message || sessionError,
        );
        setFetchError("Failed to retrieve user session.");
        return;
      }
      const userEmail = sessionData?.session?.user?.email;
      if (!userEmail) {
        console.error("User email not found in session.");
        setFetchError("User not authenticated.");
        return;
      }
      // Owner names (/api/schema-users/, ~1.6 s) need only the viewer's id,
      // so the lookup starts now and overlaps the user-row read, the fan-out
      // and the overlay instead of following them. A patient viewer still
      // asks only once there are rows to decorate, as before.
      const ownersPromise = patientView
        ? null
        : getOwnerIndex(getBackendURL(), sessionData.session.user.id);
      // Role/name/timezone + patient_* verification columns. For tenant-schema
      // users the authoritative role comes from their tenant-schema row;
      // public.users may still hold a clinical role for the same person.
      const { data: userRow, error: userError } = await fetchEffectiveUserRow({
        email: userEmail,
      });
      if (superseded()) return;
      if (userError || !userRow) {
        console.error("Error fetching user role:", userError);
        setFetchError("Failed to determine user role.");
        return;
      }
      const role = userRow.role;
      setUserRole(role);
      setCurrentUserEmail(userEmail || "");
      const effectiveViewerTimezone = userRow.timezone || viewerTimezone;
      if (userRow.timezone) setViewerTimezone(userRow.timezone);
      if (userRow.name) setCurrentUserName(userRow.name);
      console.log("[tz-debug] SchedulesTable viewer context", {
        userEmail,
        userId: sessionData?.session?.user?.id,
        role,
        viewerTimezoneFromUsers: userRow.timezone || null,
      });
      setCurrentUserId(sessionData.session.user.id);
      const isValidDate =
        selectedDate && !isNaN(new Date(selectedDate).getTime());
      const formattedDate = isValidDate
        ? new Date(selectedDate).toISOString().split("T")[0]
        : null;
      const userId = sessionData.session.user.id;
      let scheduleClient = supabase;
      let resolvedSchema = supabaseSchema;
      if (isPatientRole(role) && userRow.patient_verified_schema) {
        scheduleClient = supabase.schema(userRow.patient_verified_schema);
        resolvedSchema = userRow.patient_verified_schema;
      }
      let query = scheduleClient.from(tableName).select("*, transcripts");
      if (formattedDate) {
        query = query.eq("date_", formattedDate);
      }
      setTenantShareAllOwnerIds([]);
      setTenantShareAllOwnerRoles({});
      if (isPatientRole(role)) {
        if (userRow.patient_match_status !== "verified" || !userRow.patient_dob) {
          setLocalSchedules([]);
          setAllSchedules([]);
          if (setSchedules) setSchedules([]);
          setFetchError("Your patient portal access is pending verification.");
          return;
        }
        query = query.eq("dob", String(userRow.patient_dob).slice(0, 10));
      } else if (!hasClinicalAdminPermissions(role)) {
        // Medstudents follow the same share-all visibility model as tenant
        // users: own rows + per-row shares + rows from owners who shared-all
        // with them. Without this branch they'd hit the own-rows-only fallback
        // and never see anything a doctor (or another medstudent) shared.
        if (isInGroup(role, 'tenantSchema') || normalizeRole(role) === 'medstudent') {
          // Tenant: own rows OR in row's shared_users OR can see all rows of owners who have me in their users.shared_users
          let ownerIds = [];
          let ownerRolesMap = {};
          try {
            const backendUrl = getBackendURL();
            const res = await fetch(
              `${backendUrl}/api/shared-all-owner-ids/?user_id=${encodeURIComponent(userId)}`,
            );
            const json = await res.json().catch(() => ({}));
            if (res.ok && Array.isArray(json.owner_ids))
              ownerIds = json.owner_ids;
            if (res.ok && json.owner_roles && typeof json.owner_roles === "object")
              ownerRolesMap = json.owner_roles;
          } catch (_) {}
          setTenantShareAllOwnerIds(ownerIds);
          setTenantShareAllOwnerRoles(ownerRolesMap);
          const orParts = [
            `user_id.eq.${userId}`,
            ...sharedUsersOverlapOrClauses(userId),
          ];
          if (ownerIds.length > 0) {
            orParts.push(`user_id.in.(${ownerIds.join(",")})`);
          }
          query = query.or(orParts.join(","));
        } else {
          setTenantShareAllOwnerIds([]);
          setTenantShareAllOwnerRoles({});
          query = query.eq("user_id", userId);
        }
      }
      let data, error;
      if (hasClinicalAdminPermissions(role)) {
        // Cross-schema fan-out: clinical roles see visits across every
        // selected linked-provider schema, not just the home schema. This
        // bypasses the scheduleClient/query built above (that query is only
        // consumed by the non-clinical `else` branch below) — each schema
        // gets its own plain date-filtered query (no tenant share-all `.or()`
        // for clinical roles), run in parallel, then merged with each row
        // tagged `_schema`. A single selected schema (the default) reduces to
        // one query against the home schema, matching prior behavior.
        const providers = (selectedProviders && selectedProviders.length)
          ? selectedProviders
          : [supabaseSchema];
        const results = await Promise.all(providers.map(async (s) => {
          try {
            let q = supabase.schema(s).from(tableName).select("*, transcripts");
            if (formattedDate) q = q.eq("date_", formattedDate);
            const { data: d, error: e } = await q;
            if (e) throw e;
            return { schema: s, rows: d || [] };
          } catch (e) {
            console.warn(`[schedules] provider ${s} failed:`, e?.message || e);
            return { schema: s, rows: null };
          }
        }));
        if (superseded()) return;
        data = mergeSchemaResults(results);
        error = null;
        const failed = results.filter((r) => r.rows === null).map((r) => r.schema);
        if (failed.length) {
          setFetchError(`Couldn't load provider(s): ${failed.join(", ")}`);
        }
      } else {
        // Existing single-schema query (patient / medstudent / tenant) — unchanged.
        ({ data, error } = await query);
        if (superseded()) return;
      }
      if (error) {
        console.error("Error fetching schedules:", error.message || error);
        setFetchError("Failed to fetch schedules.");
      } else {
        console.log("🗄️ Raw database data fetched:", data);

        // Test-integration plan Phase 1c: fire-and-forget, off by
        // default (REACT_APP_RDS_SHADOW_READ_SCHEDULES). Never
        // awaited -- cannot add latency or affect anything below.
        // Groups by _schema for the cross-schema fan-out branch above,
        // or resolvedSchema for the single-schema branch (fan-out rows
        // are tagged _schema by mergeSchemaResults; single-schema rows
        // are not, so they all fall under resolvedSchema).
        const shadowRowsBySchema = {};
        (data || []).forEach((item) => {
          const sch = item._schema || resolvedSchema;
          if (!sch) return;
          if (!shadowRowsBySchema[sch]) shadowRowsBySchema[sch] = [];
          shadowRowsBySchema[sch].push(item);
        });
        shadowCompareScheduleList(shadowRowsBySchema);

        let mappedData = data.map((item, index) => {
          console.log(`🗄️ Raw database item ${item.id}:`, {
            forms_ind: item.forms_ind,
            transcripts: item.transcripts,
          });
          const mappedItem = {
            id: item.id || `item-${index}`,
            created_at: item.created_at,
            time_: item.time_ || null,
            act_time: item.act_time || null,
            date_: item.date_ || null,
            // Frozen source zone (creator's tz at insert); drives display conversion.
            timezone: item.timezone ?? null,
            reports: item.reports || null,
            file_reference_column: item.file_reference_column || null,
            more_audios: filterAudiosForVisitDate(
              item.more_audios || [],
              item.date_ || formattedDate,
              supabaseSchema,
            ),
            transcripts: filterTranscriptsForVisitDate(
              item.transcripts || [],
              item.date_ || formattedDate,
              item.more_audios || [],
              supabaseSchema,
            ),
            consent: { audio: false, messaging: false },
            forms_ind: item.forms_ind || false,
            user_id: item.user_id ?? null,
            shared_users: item.shared_users ?? [],
            // Appointment-confirmation state. These six drive the Confirm
            // column (ConfirmStatusCell) and the hub modal's tally/work
            // queue, and MUST survive this whitelist: the query is
            // select("*"), but anything not copied here reaches ResultsTable
            // as undefined, so every row would render the "U / Awaiting
            // reply" default no matter what the database says — and a manual
            // override would silently revert on the next fetch.
            // `?? null` (not `|| null`) so a legitimately falsy stored value
            // is preserved rather than coerced.
            confirmation_status: item.confirmation_status ?? null,
            confirmation_sent_at: item.confirmation_sent_at ?? null,
            confirmation_updated_at: item.confirmation_updated_at ?? null,
            confirmation_source: item.confirmation_source ?? null,
            confirmation_updated_by: item.confirmation_updated_by ?? null,
            confirmation_rules_sent: item.confirmation_rules_sent ?? null,
            _schema: item._schema,
          };
          // The value the live-transcription effect below would add (it is
          // localStorage-only), set here so a fetch changes `schedules` once
          // rather than twice: every change re-runs each [schedules] effect.
          mappedItem.live_transcription_enabled = getLiveTranscriptionSetting(
            mappedItem.id,
          );
          console.log(`📋 Mapped item ${mappedItem.id}:`, {
            forms_ind: mappedItem.forms_ind,
          });
          prompts.forEach((d) => {
            const colInItem = item[d[0]];
            mappedItem[d[0]] = colInItem || "";
          });
          return mappedItem;
        });
        mappedData = filterRowsForPatient(userRow, mappedData);
        mappedData = await hydrateScheduleRowsTimezoneSource(supabase, mappedData);
        const uniqueUserIds = [...new Set(mappedData.map((r) => r.user_id).filter(Boolean))];
        if (uniqueUserIds.length > 0) {
          try {
            const ownerIndex = await (ownersPromise ||
              getOwnerIndex(getBackendURL(), userId));
            mappedData = mappedData.map((row) => {
              const owner = ownerIndex.get(String(row.user_id || "").toLowerCase());
              if (!owner) return row;
              return {
                ...row,
                owner_name: owner.name || "",
                owner_email: owner.email || "",
              };
            });
          } catch (ownerErr) {
            console.warn("[SchedulesTable] owner display preload failed:", ownerErr);
          }
        }
        console.log("[tz-debug] SchedulesTable row timezone coverage", {
          rows: mappedData.length,
          ownerIds: uniqueUserIds.length,
          missingTimezoneSource: mappedData.filter((r) => !r.timezone_source).length,
          sampleMissing: mappedData
            .filter((r) => !r.timezone_source)
            .slice(0, 10)
            .map((r) => ({ id: r.id, user_id: r.user_id, time_: r.time_, date_: r.date_ })),
        });

        mappedData = sortObjectsByTime(mappedData, "time_", {
          dateKey: "date_",
          viewerTimezone: effectiveViewerTimezone,
        });
        // Loaded alongside the rows (fetchData); only the button texts use it.
        const nsData = await nsPromise;
        if (superseded()) return;
        const seeEntityLabel = isAdminPage
          ? ""
          : `See ${getLabel(effectiveUserRole, 'entity')}`;
        const buttonTempText = [];
        const buttonTempTextNon = [];
        mappedData.forEach((d) => {
          if (d.more_audios.length < 1) {
            if (nsData.includes(d.id) && !isAdminPage) {
              buttonTempTextNon.push("Showed up");
            } else {
              buttonTempTextNon.push(seeEntityLabel);
            }
            buttonTempText.push(seeEntityLabel);
          } else {
            buttonTempText.push(isAdminPage ? "" : "Listen To Audio");
            buttonTempTextNon.push(isAdminPage ? "" : "Listen To Audio");
          }
        });
        setDefaultButtonTexts(buttonTempText);
        setButtonTexts(buttonTempTextNon);
        console.log("🗄️ Final mapped data:", mappedData);
        mappedData = mappedData.map((r) => ({ ...r, _displayTime: convertScheduledTime(r.date_, r.time_, r.timezone_source || null, effectiveViewerTimezone) }));
        setLocalSchedules(mappedData);
        setAllSchedules(mappedData);
        setTotalPages(Math.max(1, Math.ceil(mappedData.length / itemsPerPage)));
        if (setSchedules) {
          console.log("🗄️ Calling setSchedules with mapped data");
          setSchedules(mappedData);
        }
      }
    } catch (error) {
      console.error("Error fetching schedules:", error.message || error);
      if (!superseded()) setFetchError("An unexpected error occurred.");
    } finally {
      if (!superseded()) setIsLoading(false);
    }
  };

  // Handle checkbox change for row selection
  const handleCheckboxChange = (id) => {
    if (patientView) return;
    console.log(`☑️ Checkbox changed for patient ID: ${id}`);
    setSelectedRows((prev) => {
      const newSelectedRows = { ...prev }; // Preserve existing selections
      let selectedScheduleData = null;

      if (!prev[id]) {
        // Add to selection
        newSelectedRows[id] = true;
        selectedScheduleData = schedules.find((schedule) => schedule.id === id);
        console.log(
          `☑️ Added patient to selection:`,
          selectedScheduleData?.[prompts[0][0]] || "Unknown",
        );
      } else {
        // Remove from selection
        delete newSelectedRows[id];
        console.log(`☑️ Removed patient from selection`);
      }

      console.log(`☑️ Current selected rows:`, Object.keys(newSelectedRows));
      console.log(`☑️ Selected count:`, Object.keys(newSelectedRows).length);

      if (onSelectedRowChange) {
        onSelectedRowChange({
          selection: newSelectedRows,
          scheduleData: selectedScheduleData,
        });
      }
      return newSelectedRows;
    });
  };

  // Handle row click actions
  const handleRowClick = (index) => {
    const schedule = schedules[index];
    const seeEntityLabel = `See ${getLabel(effectiveUserRole, 'entity')}`;
    if (buttonTexts[index] === "Listen To Audio") {
      const visitAudios = filterAudiosForVisitDate(
        schedule.more_audios || [],
        schedule.date_ || selectedDate,
        supabaseSchema,
      );
      const visitTranscripts = filterTranscriptsForVisitDate(
        schedule.transcripts || [],
        schedule.date_ || selectedDate,
        schedule.more_audios || [],
        supabaseSchema,
      );
      if (visitAudios.length === 0) return;
      handleAudioPlayerModal(visitAudios[0], visitAudios, visitTranscripts);
    } else if (buttonTexts[index] === seeEntityLabel) {
      const routePrefix = canAccessPage(effectiveUserRole, 'clinicPage') ? 'clinic' : 'visit';
      navigate(
        `/${routePrefix}/${getPatientPageVisitPathSegment(schedule)}?date=${selectedDate}`,
        {
          state: {
            rowData: schedule,
            userInfo: schedule,
            date_: schedule.date_,
            [prompts[0][0]]: schedule[prompts[0][0]] || "",
          },
        },
      );
    }
  };

  // Handle audio player modal
  const handleAudioPlayerModal = (audio, allAudios, transcripts = []) => {
    setAudio(audio);
    setAllAudios(allAudios);
    setAudioTranscripts(transcripts);
    setIsAudioPlayerModalOpen(true);
  };

  // Close audio player modal
  const closeAudioPlayerModal = () => {
    setIsAudioPlayerModalOpen(false);
    setAudio(undefined);
    setAllAudios([]);
    setAudioTranscripts([]);
  };

  // Add a user to shared_users for the current share row (tenant roles only).
  // If the row is part of a recurring class/meeting (same entity name + same owner), share all those rows.
  const handleAddSharedUser = async (userToAdd, roleArg) => {
    if (!shareSchedule?.id) return;
    const role = normalizeShareRole(roleArg);
    const existing = Array.isArray(shareSchedule.shared_users)
      ? shareSchedule.shared_users
      : [];
    if (sharedUsersHasViewer(existing, userToAdd.id)) return;
    const supabase = await getSupabase();
    const nameField = prompts[0][0];
    const entityName = shareSchedule[nameField];
    const hasEntityName =
      entityName != null && String(entityName).trim() !== "";

    let rowsToUpdate = [
      {
        id: shareSchedule.id,
        shared_users: existing,
      },
    ];
    if (hasEntityName) {
      const { data: siblingRows, error: fetchErr } = await supabase
        .from(tableName)
        .select("id, shared_users")
        .eq("user_id", shareSchedule.user_id)
        .eq(nameField, entityName);
      if (fetchErr) {
        toast({
          title: "Error",
          description: fetchErr.message || "Failed to find recurring rows.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      rowsToUpdate = (siblingRows || []).map((r) => ({
        id: r.id,
        shared_users: Array.isArray(r.shared_users) ? r.shared_users : [],
      }));
    }
    const rowIds = rowsToUpdate.map((r) => r.id);

    for (const row of rowsToUpdate) {
      const current = Array.isArray(row.shared_users) ? row.shared_users : [];
      const merged = mergeSharedUsersAdd(current, userToAdd.id, role);
      const { error } = await supabase
        .from(tableName)
        .update({ shared_users: merged })
        .eq("id", row.id);
      if (error) {
        toast({
          title: "Error",
          description: error.message || "Failed to share.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
    }

    let recipientShared = [];
    try {
      const { data: userRow } = await supabase
        .from("users")
        .select("shared_users")
        .eq("id", userToAdd.id)
        .single();
      if (userRow?.shared_users) {
        const parsed =
          typeof userRow.shared_users === "string"
            ? JSON.parse(userRow.shared_users)
            : userRow.shared_users;
        if (Array.isArray(parsed)) {
          recipientShared = parsed.filter((x) => typeof x === "string");
        }
      }
    } catch (_) {}
    const mergedRecipient = [...new Set([...recipientShared, ...rowIds])];
    await supabase
      .from("users")
      .update({ shared_users: mergedRecipient })
      .eq("id", userToAdd.id);

    const updatesById = {};
    rowsToUpdate.forEach((row) => {
      const current = Array.isArray(row.shared_users) ? row.shared_users : [];
      updatesById[row.id] = mergeSharedUsersAdd(
        current,
        userToAdd.id,
        role,
      );
    });
    setLocalSchedules((prev) =>
      prev.map((s) =>
        updatesById[s.id] ? { ...s, shared_users: updatesById[s.id] } : s,
      ),
    );
    if (setSchedules) {
      setSchedules((prev) =>
        prev.map((s) =>
          updatesById[s.id] ? { ...s, shared_users: updatesById[s.id] } : s,
        ),
      );
    }
    setShareSchedule((prev) =>
      prev && updatesById[prev.id]
        ? { ...prev, shared_users: updatesById[prev.id] }
        : prev,
    );
    const addId = String(userToAdd.id).toLowerCase();
    setShareModalAlreadySharedIds((prev) => new Set([...prev, addId]));
    setShareModalSharedUserRoles((prev) => ({
      ...prev,
      [addId]: role,
    }));
    toast({
      title: "Shared",
      description:
        rowIds.length > 1
          ? `Shared ${rowIds.length} recurring dates with ${userToAdd.email || userToAdd.name || "user"}.`
          : `Shared with ${userToAdd.email || userToAdd.name || "user"}.`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const handleUpdateShareModalRole = async (userId, newRole) => {
    if (!shareSchedule?.id) return;
    const role = normalizeShareRole(newRole);
    setShareModalUpdatingRoleId(userId);
    const supabase = await getSupabase();
    const nameField = prompts[0][0];
    const entityName = shareSchedule[nameField];
    const hasEntityName =
      entityName != null && String(entityName).trim() !== "";
    try {
      let rowsToUpdate = [
        {
          id: shareSchedule.id,
          shared_users: Array.isArray(shareSchedule.shared_users)
            ? shareSchedule.shared_users
            : [],
        },
      ];
      if (hasEntityName) {
        const { data: siblingRows, error: fetchErr } = await supabase
          .from(tableName)
          .select("id, shared_users")
          .eq("user_id", shareSchedule.user_id)
          .eq(nameField, entityName);
        if (fetchErr) throw fetchErr;
        rowsToUpdate = (siblingRows || []).map((r) => ({
          id: r.id,
          shared_users: Array.isArray(r.shared_users) ? r.shared_users : [],
        }));
      }
      const updatesById = {};
      for (const row of rowsToUpdate) {
        const merged = mergeSharedUsersAdd(
          row.shared_users,
          userId,
          role,
        );
        updatesById[row.id] = merged;
        const { error } = await supabase
          .from(tableName)
          .update({ shared_users: merged })
          .eq("id", row.id);
        if (error) throw error;
      }
      setLocalSchedules((prev) =>
        prev.map((s) =>
          updatesById[s.id] ? { ...s, shared_users: updatesById[s.id] } : s,
        ),
      );
      if (setSchedules) {
        setSchedules((prev) =>
          prev.map((s) =>
            updatesById[s.id] ? { ...s, shared_users: updatesById[s.id] } : s,
          ),
        );
      }
      setShareSchedule((prev) =>
        prev && updatesById[prev.id]
          ? { ...prev, shared_users: updatesById[prev.id] }
          : prev,
      );
      const rid = String(userId).toLowerCase();
      setShareModalSharedUserRoles((prev) => ({ ...prev, [rid]: role }));
      toast({
        title: "Permission updated",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Error",
        description: err?.message || "Failed to update permission.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setShareModalUpdatingRoleId(null);
    }
  };

  // Add one user to "share all rows" — used as the onShare callback for the
  // ShareRowModal-based share-all dialog. Each click of a per-row Share button
  // commits a single user; no bulk-select step.
  const handleShareAllSingleUser = async (user, role) => {
    if (!currentUserId || !user?.id) return;
    const supabase = await getSupabase();
    setShareAllSaving(true);
    try {
      const { data: myRow } = await supabase
        .from("users")
        .select("shared_users")
        .eq("id", currentUserId)
        .single();
      const { entries: existing } = parseOwnerShareAllEntries(myRow?.shared_users);
      const byId = new Map(existing.map((e) => [e.id, e]));
      if (!byId.has(user.id)) {
        byId.set(user.id, {
          id: user.id,
          role: normalizeShareRole(role || shareAllDefaultRole),
        });
        const next = serializeOwnerShareAllEntries(Array.from(byId.values()));
        const { error } = await supabase
          .from("users")
          .update({ shared_users: next })
          .eq("id", currentUserId);
        if (error) throw error;
      }
      setShareAllRecipientEntries(Array.from(byId.values()));
      toast({
        title: "Shared all rows",
        description: `${user.email || user.name || "User"} can now see all your rows (past and future).`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      if (typeof refetchSchedules === "function") refetchSchedules();
      else setRefreshData((prev) => !prev);
    } catch (err) {
      toast({
        title: "Error",
        description: err?.message || "Failed to share all rows.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setShareAllSaving(false);
    }
  };

  const handleUpdateShareAllRecipientRole = async (userId, newRole) => {
    if (!currentUserId) return;
    const role = normalizeShareRole(newRole);
    setShareAllUpdatingRoleId(userId);
    const supabase = await getSupabase();
    try {
      const { data: myRow } = await supabase
        .from("users")
        .select("shared_users")
        .eq("id", currentUserId)
        .single();
      const { entries } = parseOwnerShareAllEntries(myRow?.shared_users);
      const byId = new Map(entries.map((e) => [e.id, e]));
      if (!byId.has(userId)) return;
      byId.set(userId, { id: userId, role });
      const next = serializeOwnerShareAllEntries(Array.from(byId.values()));
      const { error } = await supabase
        .from("users")
        .update({ shared_users: next })
        .eq("id", currentUserId);
      if (error) throw error;
      setShareAllRecipientEntries(Array.from(byId.values()));
      toast({
        title: "Permission updated",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Error",
        description: err?.message || "Failed to update permission.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setShareAllUpdatingRoleId(null);
    }
  };

  // Revoke "share all" for one user: they keep access to past rows but won't see future rows.
  const handleRevokeShareAll = async (userId) => {
    if (!currentUserId) return;
    const supabase = await getSupabase();
    setShareAllRevokingId(userId);
    try {
      const { data: myRow } = await supabase
        .from("users")
        .select("shared_users")
        .eq("id", currentUserId)
        .single();
      const { entries } = parseOwnerShareAllEntries(myRow?.shared_users);
      const filtered = entries.filter((e) => e.id !== userId);
      const next = serializeOwnerShareAllEntries(filtered);
      const { error } = await supabase
        .from("users")
        .update({ shared_users: next })
        .eq("id", currentUserId);
      if (error) throw error;
      setShareAllRecipientEntries(filtered);
      toast({
        title: "Access updated",
        description:
          "They will no longer see your future rows; past rows are unchanged.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      if (typeof refetchSchedules === "function") refetchSchedules();
      else setRefreshData((prev) => !prev);
    } catch (err) {
      toast({
        title: "Error",
        description: err?.message || "Failed to revoke access.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setShareAllRevokingId(null);
    }
  };

  // Remove a user from shared_users for the current share row (and all sibling recurring rows)
  const handleRemoveSharedUser = async (userToRemove) => {
    if (!shareSchedule?.id) return;
    const supabase = await getSupabase();
    const nameField = prompts[0][0];
    const entityName = shareSchedule[nameField];
    const hasEntityName =
      entityName != null && String(entityName).trim() !== "";
    setShareModalRemovingId(userToRemove.id);
    try {
      let rowsToUpdate = [
        {
          id: shareSchedule.id,
          shared_users: Array.isArray(shareSchedule.shared_users)
            ? shareSchedule.shared_users
            : [],
        },
      ];
      if (hasEntityName) {
        const { data: siblingRows, error: fetchErr } = await supabase
          .from(tableName)
          .select("id, shared_users")
          .eq("user_id", shareSchedule.user_id)
          .eq(nameField, entityName);
        if (!fetchErr)
          rowsToUpdate = (siblingRows || []).map((r) => ({
            id: r.id,
            shared_users: Array.isArray(r.shared_users) ? r.shared_users : [],
          }));
      }
      const updatesById = {};
      for (const row of rowsToUpdate) {
        const current = Array.isArray(row.shared_users) ? row.shared_users : [];
        const updated = mergeSharedUsersRemove(current, userToRemove.id);
        updatesById[row.id] = updated;
        await supabase
          .from(tableName)
          .update({ shared_users: updated })
          .eq("id", row.id);
      }
      setLocalSchedules((prev) =>
        prev.map((s) =>
          updatesById[s.id] ? { ...s, shared_users: updatesById[s.id] } : s,
        ),
      );
      if (setSchedules) {
        setSchedules((prev) =>
          prev.map((s) =>
            updatesById[s.id] ? { ...s, shared_users: updatesById[s.id] } : s,
          ),
        );
      }
      setShareSchedule((prev) =>
        prev && updatesById[prev.id]
          ? { ...prev, shared_users: updatesById[prev.id] }
          : prev,
      );
      const remId = String(userToRemove.id).toLowerCase();
      setShareModalAlreadySharedIds((prev) => {
        const next = new Set(prev);
        for (const x of [...next]) {
          if (String(x).toLowerCase() === remId) next.delete(x);
        }
        return next;
      });
      setShareModalSharedUserRoles((prev) => {
        const n = { ...prev };
        delete n[remId];
        for (const k of Object.keys(n)) {
          if (k.toLowerCase() === remId) delete n[k];
        }
        return n;
      });
      toast({
        title: "Access Removed",
        description: `Removed access for ${userToRemove.email || userToRemove.name || "user"}.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Error",
        description: err?.message || "Failed to remove access.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setShareModalRemovingId(null);
    }
  };

  // Handle recording audio
  const handleRecordAudio = (data) => {
    if (patientView) return;
    if (!data) return;
    setPendingRecordingRow(data);
  };

  const handleStartRecordingForRow = (row) => {
    if (!row) return;

    // Ensure bubble has up-to-date patient context before opening (same pattern as AudioRecorder in PatientPage)
    window.dispatchEvent(
      new CustomEvent("patientContextUpdate", {
        detail: {
          patientId: row.id,
          patientData: [row],
        },
      }),
    );

    // Single start path across the app: open via RecordingBubble
    const openEvent = new CustomEvent("recordingBubbleOpen", {
      detail: { source: "SchedulesTable", handled: false },
    });
    window.dispatchEvent(openEvent);

    // Note: We don't set pendingRecordingRow here because RecordingBubble handles
    // all recording state via the recordingBubbleOpen event and patientContextUpdate event.
    // Setting pendingRecordingRow would cause conflicts with SchedulesTable's own
    // useExternalRecording hook instance.
  };

  // Debug function to check and manually delete a document file
  const debugDeleteDocument = async (patientId, patientName, date) => {
    const supabase = await getSupabase();
    const year = date
      ? date.split("-")[0]
      : new Date().getFullYear().toString();

    // Use the already-parsed active schema from supabaseClient (single source of truth)
    const userSchema = supabaseSchema || "public";

    const filename = `${patientName}-${patientId}.json`;
    const documentPath = await generateMedADocsPath(
      year,
      date,
      filename,
      userSchema,
    );

    console.log(`🔍 Debug: Checking document at path: ${documentPath}`);

    // Check if file exists
    const { data: fileData, error: checkError } = await supabase.storage
      .from(bucketName_files)
      .download(documentPath);

    if (checkError) {
      console.log(`🔍 Debug: File not found or error:`, checkError.message);
    } else {
      console.log(`🔍 Debug: File exists, attempting to delete...`);

      // Delete file and clean up any file_classification_cache entry
      const { errors } = await deleteFilesWithCacheCleanup(
        supabase,
        bucketName_files,
        [documentPath],
      );
      if (errors.length > 0) {
        console.error(`🔍 Debug: Error deleting file:`, documentPath);
      } else {
        console.log(`🔍 Debug: Successfully deleted file: ${documentPath}`);
      }
    }
  };

  // Handle removing selected patients
  const handleRemovePatient = async () => {
    if (patientView) return;
    const idsToRemove = Object.keys(selectedRows);
    console.log(`🗑️ handleRemovePatient called for ${idsToRemove.length} row(s):`, idsToRemove);
    if (idsToRemove.length === 0) {
      console.log(`🗑️ No rows selected, returning early`);
      return;
    }

    // Partition by permission first. A shared viewer/editor may legitimately
    // have some deletable and some non-deletable rows selected; remove what we
    // can and name the rest, rather than failing the whole action silently.
    const allowedIds = [];
    const blockedIds = [];
    idsToRemove.forEach((id) => {
      if (canDeleteRowId(id)) allowedIds.push(id);
      else blockedIds.push(id);
    });

    if (allowedIds.length === 0) {
      toast({
        title: "Cannot remove",
        description:
          blockedIds.length === 1
            ? "You do not have permission to remove this row (viewer or editor shared access)."
            : `You do not have permission to remove any of the ${blockedIds.length} selected rows (viewer or editor shared access).`,
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const supabase = await getSupabase();

    // PostgREST puts .in() lists in the URL, so a large selection would blow the
    // length limit. Chunk every id-keyed request.
    const chunkIds = (ids, size = 50) => {
      const out = [];
      for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
      return out;
    };

    try {
      // Find the EHR_ID field index
      const ehrIdIndex = prompts.findIndex((p) => p[0] === "EHR_ID");
      const ehrIdField = ehrIdIndex >= 0 ? prompts[ehrIdIndex][0] : null;

      console.log(`🗑️ EHR_ID field name: ${ehrIdField}`);

      const rowsToRemove = [];
      for (const chunk of chunkIds(allowedIds)) {
        // eslint-disable-next-line no-await-in-loop
        const { data, error: fetchError } = await supabase
          .from(tableName)
          .select(
            `id, ${prompts[0][0]}, date_${ehrIdField ? `, ${ehrIdField}` : ""}`,
          )
          .in("id", chunk);

        if (fetchError) {
          console.error("Error fetching row data:", fetchError);
          toast({
            title: "Error",
            description: "Failed to fetch row data for document cleanup.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          return;
        }
        rowsToRemove.push(...(data || []));
      }

      console.log(`🗑️ Fetched ${rowsToRemove.length} row(s) for deletion`);

      // Build every document path up front so the storage delete is one call.
      const documentPaths = [];
      const userSchema = supabaseSchema || "public";
      for (const rowToRemove of rowsToRemove) {
        try {
          const year = rowToRemove.date_
            ? rowToRemove.date_.split("-")[0]
            : new Date().getFullYear().toString();
          const date = rowToRemove.date_ || getDefaultDateString();
          const patientName = rowToRemove[prompts[0][0]] || "SOAP_note";

          // Use EHR_ID if available, otherwise fall back to database id
          const patientIdForFile =
            ehrIdField && rowToRemove[ehrIdField]
              ? rowToRemove[ehrIdField]
              : rowToRemove.id;

          console.log(`🗑️ Row data for deletion:`, {
            databaseId: rowToRemove.id,
            ehrId: ehrIdField ? rowToRemove[ehrIdField] : "N/A",
            usedId: patientIdForFile,
            name: patientName,
            date: date,
            year: year,
          });

          const filename = `${patientName}-${patientIdForFile}.json`;
          // eslint-disable-next-line no-await-in-loop
          const documentPath = await generateMedADocsPath(
            year,
            date,
            filename,
            userSchema,
          );

          // The existence probe is a debugging aid; running it once per row
          // would mean dozens of extra storage listings on a bulk delete.
          if (rowsToRemove.length === 1) {
            // eslint-disable-next-line no-await-in-loop
            await debugDeleteDocument(patientIdForFile, patientName, date);
          }

          documentPaths.push(documentPath);
        } catch (error) {
          console.error(
            `❌ Error building document path for row ${rowToRemove.id}:`,
            error,
          );
        }
      }

      if (documentPaths.length > 0) {
        console.log(`🗑️ Attempting to delete ${documentPaths.length} document(s)`);

        // Delete the document files from storage and clean up cache
        const { errors: deleteErrors } = await deleteFilesWithCacheCleanup(
          supabase,
          bucketName_files,
          documentPaths,
        );

        if (deleteErrors.length > 0) {
          console.log(
            `📄 ${deleteErrors.length} document file(s) could not be deleted (may not exist)`,
          );
        } else {
          console.log(`✅ Successfully deleted ${documentPaths.length} document file(s)`);
        }

        // Also delete the rows from the documents table
        try {
          for (const pathChunk of chunkIds(documentPaths)) {
            // eslint-disable-next-line no-await-in-loop
            const { error: docTableError } = await supabase
              .from("documents")
              .delete()
              .in("path", pathChunk);
            if (docTableError) {
              console.warn(
                `⚠️ Could not delete documents table rows:`,
                docTableError,
              );
            }
          }
        } catch (docTableErr) {
          console.warn(`⚠️ Error deleting documents table rows:`, docTableErr);
        }
      }

      // Now delete the records from the database
      const deletedIds = [];
      let deleteError = null;
      for (const chunk of chunkIds(allowedIds)) {
        // eslint-disable-next-line no-await-in-loop
        const { error } = await supabase
          .from(tableName)
          .delete()
          .in("id", chunk);
        if (error) {
          deleteError = error;
          break;
        }
        deletedIds.push(...chunk);
      }

      if (deletedIds.length > 0) {
        // Drop whatever actually went, even if a later chunk failed, so the
        // table never shows rows that are already gone from the database.
        const removed = new Set(deletedIds.map(String));
        const updatedSchedules = schedules.filter((s) => !removed.has(String(s.id)));
        setLocalSchedules(updatedSchedules);
        if (setSchedules) setSchedules(updatedSchedules);
        setSelectedRows({});
        setTotalPages(
          Math.max(1, Math.ceil(updatedSchedules.length / itemsPerPage)),
        );
      }

      if (deleteError) {
        toast({
          title: "Error",
          description:
            deletedIds.length > 0
              ? `Removed ${deletedIds.length} of ${allowedIds.length} rows, then failed: ${deleteError.message}`
              : "Failed to remove from database.",
          status: "error",
          duration: 7000,
          isClosable: true,
        });
      } else {
        const entity = getLabel(effectiveUserRole, 'entity');
        const skipped = blockedIds.length
          ? ` ${blockedIds.length} skipped (no permission).`
          : "";
        toast({
          title: "Success",
          description:
            deletedIds.length === 1
              ? `${entity} and their documents removed successfully.${skipped}`
              : `${deletedIds.length} rows and their documents removed successfully.${skipped}`,
          status: blockedIds.length ? "warning" : "success",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error in handleRemovePatient:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred while removing.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Prepare data for CSV export
  const prepareCSVData = () => {
    // Export what's on screen: with no filter active this is the whole list,
    // exactly as before.
    const nonGrayedSchedules = visibleSchedules.filter((schedule) => {
      // Only filter out noShows, keep everything else
      const isNoShow = noShows.includes(schedule.id);
      return !isNoShow;
    });

    const formatTimeForCsv = (schedule) => {
      const s = convertScheduledTime(
        schedule.date_,
        schedule.time_,
        schedule.timezone_source ?? null,
        viewerTimezone,
      );
      return s === "N/A" ? "" : s;
    };
    const headers = ["time", ...prompts.map((p) => p[1]), "Forms"];
    const csvData = [
      headers,
      ...nonGrayedSchedules.map((schedule) => {
        return [
          formatTimeForCsv(schedule),
          ...prompts.map((p) => schedule[p[0]] || ""),
          schedule.forms_ind ? "1" : "",
        ];
      }),
    ];
    return csvData;
  };

  // Close modals
  const closeModal = () => {
    setIsAudioPlayerModalOpen(false);
    setAudio(undefined);
    setScheId(undefined);
  };

  // Handle viewing reports
  const handleViewReports = async (folderPath, patientId = null) => {
    console.log("🔍 Viewing reports at:", folderPath);
    console.log("🔍 Folder path type:", typeof folderPath);
    console.log("🔍 Folder path value:", folderPath);
    console.log("🔍 Patient ID:", patientId);

    // Check if folder path contains email instead of EHR_ID
    const hasEmail =
      folderPath &&
      (folderPath.includes("@") ||
        /_[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(folderPath));

    if (hasEmail) {
      console.warn(
        "⚠️ WARNING: Folder path contains email address instead of EHR_ID!",
      );
      console.warn("   This patient's 'reports' field needs to be updated.");
      console.warn("   Path:", folderPath);

      // Automatically fix the reports field if patient ID is available
      if (patientId) {
        try {
          console.log("🔧 Attempting to fix patient reports field...");
          // Get backend URL based on hostname
          const hostname = window.location.hostname;
          const baseUrl =
            hostname === "localhost"
              ? BASE_URLS.local.backend
              : BASE_URLS.render.backend;
          const response = await fetch(`${baseUrl}/api/fix-patient-reports/`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ patient_id: patientId }),
          });

          const result = await response.json();

          if (response.ok && result.status === "success") {
            console.log("✅ Successfully fixed patient reports field:", result);
            toast({
              title: "Reports field fixed",
              description: `Updated to use EHR_ID instead of email. New path: ${result.reports_path}`,
              status: "success",
              duration: 5000,
              isClosable: true,
            });

            // Use the fixed path instead of the old one
            folderPath = result.reports_path;
            console.log("🔧 Using fixed folder path:", folderPath);

            // Update the schedule's reports field in local state if possible
            // (This will be reflected on next render)
          } else {
            // Check if it's a "patient not found" error (404)
            // In this case, we can still show the reports, so don't show an error toast
            const isPatientNotFound =
              response.status === 404 ||
              (result.error &&
                result.error.toLowerCase().includes("not found"));

            if (isPatientNotFound) {
              // Patient not found - this might be a schedule ID instead of patient ID
              // Log a warning but continue - reports can still be viewed
              console.warn(
                "⚠️ Patient not found in database (might be schedule ID). Continuing to show reports...",
                result.error,
              );
              // Don't show error toast - reports can still be viewed
            } else {
              // Other errors - show error toast
              console.error("❌ Failed to fix reports field:", result);
            }
          }
        } catch (error) {
          console.error("❌ Error fixing reports field:", error);
          toast({
            title: "Error fixing reports field",
            description: error.message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      } else {
        console.warn(
          "⚠️ Patient ID not available, cannot auto-fix reports field",
        );
        toast({
          title: "Reports field needs fixing",
          description:
            "Patient's reports field contains email. Please reassign a fax to fix it.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      }
    }

    const supabase = await getSupabase();

    setCurrentFolderPath(folderPath);
    if (!folderPath) {
      console.log("❌ No folder path provided");
      setCurrentFolderFiles([]);
      setModalIsOpen(true);
      return;
    }

    try {
      console.log("🔍 Making Supabase request to list files at:", folderPath);
      console.log("🔍 Bucket name:", bucketName_files);

      // Parse the folder path to extract patient name
      const pathParts = folderPath.split("/");
      let basePath = "";
      let patientFolder = "";
      let dateFolder = "";

      if (pathParts.length >= 3) {
        basePath = pathParts.slice(0, -2).join("/"); // "New/Reports"
        patientFolder = pathParts[pathParts.length - 2];
        dateFolder = pathParts[pathParts.length - 1];
      }

      // Extract patient name (everything before the first underscore)
      // This handles: "Li Garrido, DIego_123", "Li Garrido, DIego_email@example.com", "Li Garrido, DIego_"
      const patientNameMatch = patientFolder
        ? patientFolder.match(/^(.+?)_/)
        : null;
      const patientName = patientNameMatch
        ? patientNameMatch[1]
        : patientFolder;

      console.log(
        `🔍 Extracted patient name: "${patientName}" from folder: "${patientFolder}"`,
      );
      console.log(`🔍 Base path: "${basePath}", Date folder: "${dateFolder}"`);

      const fileMap = new Map(); // Use Map to deduplicate by name, storing file with folder path

      // 1. Fetch from the original folder path first (this should work for any folder structure)
      console.log(
        `🔍 Step 1: Attempting to list files from original path: ${folderPath}`,
      );
      const { data: dataOriginal, error: errorOriginal } =
        await supabase.storage.from(bucketName_files).list(folderPath);

      if (!errorOriginal && dataOriginal) {
        const filesOnly = dataOriginal.filter((item) =>
          item.name.includes("."),
        );
        filesOnly.forEach((file) => {
          if (!fileMap.has(file.name)) {
            fileMap.set(file.name, { ...file, actualFolderPath: folderPath });
          }
        });
        console.log(
          `✅ Fetched ${filesOnly.length} files from original folder: ${folderPath}`,
        );
      } else if (errorOriginal) {
        console.warn(
          `⚠️ Error fetching from original folder: ${errorOriginal.message}`,
        );
        console.warn(`⚠️ Error details:`, errorOriginal);
      }

      // 1b. Also try to directly access the email-based folder path if it exists
      // Fetch patient's email from database to construct the email folder path
      if (patientId && patientName && dateFolder) {
        try {
          const { data: patientData, error: patientError } = await supabase
            .from(tableName)
            .select("email")
            .eq("id", patientId)
            .single();

          if (!patientError && patientData && patientData.email) {
            const patientEmail = patientData.email;
            console.log(`🔍 Step 1b: Found patient email: ${patientEmail}`);

            // Try variations of the email folder name
            const emailFolderVariations = [
              `${patientName}_ ${patientEmail}`, // Space after underscore
              `${patientName}_${patientEmail}`, // No space
              `${patientName} ${patientEmail}`, // Space instead of underscore
            ];

            for (const emailFolderName of emailFolderVariations) {
              const emailFolderPath = `${basePath}/${emailFolderName}/${dateFolder}`;
              console.log(
                `🔍 Step 1b: Trying email folder path: ${emailFolderPath}`,
              );

              const { data: emailFolderData, error: emailFolderError } =
                await supabase.storage
                  .from(bucketName_files)
                  .list(emailFolderPath);

              if (!emailFolderError && emailFolderData) {
                const emailFilesOnly = emailFolderData.filter((item) =>
                  item.name.includes("."),
                );
                if (emailFilesOnly.length > 0) {
                  emailFilesOnly.forEach((file) => {
                    // Prioritize files from email folder (replace if exists)
                    fileMap.set(file.name, {
                      ...file,
                      actualFolderPath: emailFolderPath,
                    });
                  });
                  console.log(
                    `✅ Fetched ${emailFilesOnly.length} files from email folder: ${emailFolderPath}`,
                  );
                  break; // Found it, no need to try other variations
                } else {
                  console.log(
                    `⚠️ Email folder path exists but is empty (0 files): ${emailFolderPath}`,
                  );
                  // Check if the parent folder exists and list its date folders
                  const parentEmailFolderPath = `${basePath}/${emailFolderName}`;
                  const { data: parentData, error: parentError } =
                    await supabase.storage
                      .from(bucketName_files)
                      .list(parentEmailFolderPath);

                  if (!parentError && parentData) {
                    const dateFoldersInEmailFolder = parentData.filter(
                      (item) => !item.name.includes("."),
                    );
                    console.log(
                      `🔍 Date folders in email folder ${parentEmailFolderPath}:`,
                      dateFoldersInEmailFolder.map((d) => d.name),
                    );
                    // Check if 01-02-26 exists
                    const targetDateExists = dateFoldersInEmailFolder.some(
                      (d) => d.name === dateFolder,
                    );
                    if (!targetDateExists) {
                      console.log(
                        `⚠️ Date folder ${dateFolder} does not exist in email folder. Available dates:`,
                        dateFoldersInEmailFolder.map((d) => d.name),
                      );
                    }
                  }
                  // Don't break - continue trying other variations in case the folder name is slightly different
                }
              } else if (emailFolderError) {
                console.log(
                  `⚠️ Email folder path not found: ${emailFolderPath} (error: ${emailFolderError.message})`,
                );
              }
            }
          } else if (patientError) {
            console.warn(`⚠️ Could not fetch patient email:`, patientError);
          }
        } catch (error) {
          console.warn(`⚠️ Error fetching patient email:`, error);
        }
      }

      // 2. List all folders in basePath and find all folders starting with patient name
      // Also check if email folder exists directly
      if (basePath && patientName) {
        console.log(`🔍 Step 2: Listing all folders in base path: ${basePath}`);
        const { data: allFolders, error: listError } = await supabase.storage
          .from(bucketName_files)
          .list(basePath);

        // Also try to directly access the email folder if we have patient email
        if (patientId && dateFolder) {
          try {
            const { data: patientEmailData, error: patientEmailError } =
              await supabase
                .from(tableName)
                .select("email")
                .eq("id", patientId)
                .single();

            if (
              !patientEmailError &&
              patientEmailData &&
              patientEmailData.email
            ) {
              const patientEmail = patientEmailData.email;
              // Try to list the email folder directly to see if it exists
              const emailFolderNameVariations = [
                `${patientName}_ ${patientEmail}`,
                `${patientName}_${patientEmail}`,
                `${patientName} ${patientEmail}`,
              ];

              for (const emailFolderName of emailFolderNameVariations) {
                const emailFolderParentPath = `${basePath}/${emailFolderName}`;
                const {
                  data: emailFolderParentData,
                  error: emailFolderParentError,
                } = await supabase.storage
                  .from(bucketName_files)
                  .list(emailFolderParentPath);

                if (!emailFolderParentError && emailFolderParentData) {
                  console.log(
                    `🔍 Found email folder directly: ${emailFolderParentPath}`,
                  );
                  console.log(
                    `🔍 Date folders in email folder:`,
                    emailFolderParentData
                      .filter((d) => !d.name.includes("."))
                      .map((d) => d.name),
                  );

                  // Check if 01-02-26 exists in this email folder
                  const targetDateFolder = emailFolderParentData.find(
                    (d) => !d.name.includes(".") && d.name === dateFolder,
                  );
                  if (targetDateFolder) {
                    const emailDateFolderPath = `${emailFolderParentPath}/${dateFolder}`;
                    const { data: emailDateFiles, error: emailDateError } =
                      await supabase.storage
                        .from(bucketName_files)
                        .list(emailDateFolderPath);

                    if (!emailDateError && emailDateFiles) {
                      const emailDateFilesOnly = emailDateFiles.filter((f) =>
                        f.name.includes("."),
                      );
                      emailDateFilesOnly.forEach((file) => {
                        fileMap.set(file.name, {
                          ...file,
                          actualFolderPath: emailDateFolderPath,
                        });
                      });
                      console.log(
                        `✅ Fetched ${emailDateFilesOnly.length} files from email folder date: ${emailDateFolderPath}`,
                      );
                    }
                  }
                  break; // Found the email folder, no need to try other variations
                }
              }
            }
          } catch (error) {
            console.warn(`⚠️ Error checking email folder directly:`, error);
          }
        }

        if (!listError && allFolders) {
          console.log(`🔍 Found ${allFolders.length} total items in base path`);

          // Debug: Log all folders that might be related to this patient
          const patientNameWords = patientName
            .toLowerCase()
            .split(/[\s,]+/)
            .filter((w) => w.length > 2);
          const potentiallyRelatedFolders = allFolders.filter((folder) => {
            if (folder.name.includes(".")) return false; // Skip files
            const folderNameLower = folder.name.toLowerCase();
            // Check if folder contains any significant words from patient name
            return patientNameWords.some((word) =>
              folderNameLower.includes(word),
            );
          });
          console.log(
            `🔍 Potentially related folders (containing patient name words):`,
            potentiallyRelatedFolders.map((f) => f.name),
          );
          console.log(
            `🔍 Looking for folders matching patient name: "${patientName}"`,
          );
          console.log(
            `🔍 All folder names in base path:`,
            allFolders.filter((f) => !f.name.includes(".")).map((f) => f.name),
          );

          // Also check for email-based folders specifically
          // Try to find folders that contain the patient's email
          let emailFolders = [];
          if (patientId) {
            try {
              const { data: patientData, error: patientError } = await supabase
                .from(tableName)
                .select("email")
                .eq("id", patientId)
                .single();

              if (!patientError && patientData && patientData.email) {
                const patientEmail = patientData.email.toLowerCase();
                emailFolders = allFolders.filter((folder) => {
                  if (folder.name.includes(".")) return false;
                  const folderNameLower = folder.name.toLowerCase();
                  return (
                    folderNameLower.includes("@") &&
                    folderNameLower.includes(patientEmail.split("@")[0])
                  ); // Match email username part
                });
                console.log(
                  `🔍 Folders containing patient email (${patientData.email}):`,
                  emailFolders.map((f) => f.name),
                );
              }
            } catch (error) {
              console.warn(
                `⚠️ Error fetching patient email for folder search:`,
                error,
              );
            }
          }

          // Also check for any folders with email addresses that match patient name
          const generalEmailFolders = allFolders.filter((folder) => {
            if (folder.name.includes(".")) return false;
            return (
              folder.name.toLowerCase().includes("@") &&
              folder.name.toLowerCase().includes(patientNameWords[0] || "")
            );
          });
          console.log(
            `🔍 Folders containing email addresses (general):`,
            generalEmailFolders.map((f) => f.name),
          );

          // Find all folders that start with the patient name followed by underscore
          // Use case-insensitive matching to handle variations
          // Also handle typos and variations in the name
          const patientNameLower = patientName.toLowerCase().trim();
          // Normalize: remove extra spaces, handle commas
          const normalizedPatientName = patientNameLower
            .replace(/\s+/g, " ")
            .replace(/\s*,\s*/g, ", ");

          const matchingFolders = allFolders.filter((folder) => {
            // Skip files (those with extensions)
            if (folder.name.includes(".")) return false;

            // Normalize folder name: handle spaces after underscores (e.g., "DIego_ diego" -> "DIego_diego")
            // Also normalize multiple spaces, but preserve the original for matching
            const originalFolderName = folder.name;
            let normalizedFolderName = folder.name
              .replace(/\s*_\s*/g, "_")
              .replace(/\s+/g, " ");
            const folderNameLower = normalizedFolderName.toLowerCase();

            // Extract base name from folder (everything before first underscore or space after underscore)
            // Try to split by underscore first, but also handle cases where there's a space after underscore
            let folderBaseName = "";
            if (folderNameLower.includes("_")) {
              folderBaseName = folderNameLower.split("_")[0].trim();
            } else if (
              originalFolderName.toLowerCase().match(/^(.+?)\s+[a-z0-9@]/)
            ) {
              // Handle case like "Li Garrido, DIego diegoligarrido@gmail.com" (space instead of underscore)
              folderBaseName = originalFolderName
                .toLowerCase()
                .match(/^(.+?)\s+[a-z0-9@]/)[1]
                .trim();
            }

            const normalizedFolderBaseName = folderBaseName
              .replace(/\s+/g, " ")
              .replace(/\s*,\s*/g, ", ");

            // Check if folder starts with patient name followed by underscore (with or without space)
            // Try multiple variations including space after underscore
            const exactMatch1 = folderNameLower.startsWith(
              `${normalizedPatientName}_`,
            );
            const exactMatch2 = originalFolderName
              .toLowerCase()
              .startsWith(`${normalizedPatientName}_`);
            const exactMatch3 = originalFolderName
              .toLowerCase()
              .startsWith(`${normalizedPatientName} `);
            const exactMatch4 = folderNameLower.startsWith(
              `${normalizedPatientName} `,
            );
            // Handle case where there's a space AFTER the underscore: "DIego_ diego@email.com"
            const exactMatch5 = originalFolderName
              .toLowerCase()
              .match(
                new RegExp(
                  `^${normalizedPatientName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*_\\s+`,
                ),
              );
            // Also check if it matches the base name exactly (for folders like "Li Garrido, DIego_ diego@email.com")
            const exactMatch6 =
              normalizedFolderBaseName === normalizedPatientName &&
              originalFolderName.toLowerCase().includes("_");

            const exactMatch =
              exactMatch1 ||
              exactMatch2 ||
              exactMatch3 ||
              exactMatch4 ||
              !!exactMatch5 ||
              exactMatch6;

            // Check if base names are very similar (handle typos like "Dlego" vs "Diego")
            // Compare normalized versions
            const baseNameMatch =
              normalizedFolderBaseName === normalizedPatientName;

            // Also check if they share significant parts (last name + first name start)
            // Extract last name and first name parts
            const patientParts = normalizedPatientName
              .split(/[,\s]+/)
              .filter((p) => p.length > 1);
            const folderParts = normalizedFolderBaseName
              .split(/[,\s]+/)
              .filter((p) => p.length > 1);

            // If we have at least 2 parts, check if first parts match
            const partsMatch =
              patientParts.length >= 2 &&
              folderParts.length >= 2 &&
              patientParts[0].toLowerCase() === folderParts[0].toLowerCase() &&
              (patientParts[1]
                .toLowerCase()
                .startsWith(folderParts[1].toLowerCase().substring(0, 3)) ||
                folderParts[1]
                  .toLowerCase()
                  .startsWith(patientParts[1].toLowerCase().substring(0, 3)));

            // Also check if folder contains email and matches patient name base
            const hasEmail = originalFolderName.toLowerCase().includes("@");
            const emailMatch = hasEmail && baseNameMatch;

            const matches =
              exactMatch || baseNameMatch || partsMatch || emailMatch;

            if (matches) {
              console.log(
                `✅ Matched folder: "${originalFolderName}" (normalized: "${normalizedFolderName}", base: "${normalizedFolderBaseName}", email: ${hasEmail})`,
              );
            }

            return matches;
          });

          // Also include email folders in the matching folders
          const allMatchingFolders = [
            ...new Set([...matchingFolders, ...emailFolders]),
          ];

          console.log(
            `🔍 Found ${matchingFolders.length} folders starting with "${patientName}_":`,
            matchingFolders.map((f) => f.name),
          );
          console.log(
            `🔍 Found ${emailFolders.length} email-based folders:`,
            emailFolders.map((f) => f.name),
          );
          console.log(
            `🔍 Total matching folders: ${allMatchingFolders.length}`,
          );

          // For each matching folder, check all date subfolders
          // Prioritize the current date folder first, then check others
          for (const folder of allMatchingFolders) {
            const patientFolderPath = `${basePath}/${folder.name}`;
            console.log(`🔍 Checking patient folder: ${patientFolderPath}`);

            // List all date folders in this patient folder
            const { data: dateFolders, error: dateFoldersError } =
              await supabase.storage
                .from(bucketName_files)
                .list(patientFolderPath);

            if (!dateFoldersError && dateFolders) {
              console.log(
                `🔍 Found ${dateFolders.length} items in ${patientFolderPath}:`,
                dateFolders.map((d) => d.name),
              );

              // Filter to ONLY show files from the current date folder (01-02-26)
              // Don't show files from other dates
              const currentDateFolders = dateFolders.filter((item) => {
                // Skip files (those with extensions)
                if (item.name.includes(".")) return false;
                // Only include the current date folder
                return item.name === dateFolder;
              });

              console.log(`🔍 Looking for date folder: "${dateFolder}"`);
              console.log(
                `🔍 Available date folders:`,
                dateFolders
                  .filter((d) => !d.name.includes("."))
                  .map((d) => d.name),
              );
              console.log(
                `🔍 Filtered to current date folders:`,
                currentDateFolders.map((d) => d.name),
              );

              // Check each date folder for files (should only be the current date)
              for (const dateFolderItem of currentDateFolders) {
                const fullDateFolderPath = `${patientFolderPath}/${dateFolderItem.name}`;
                console.log(
                  `🔍 Checking date folder: ${fullDateFolderPath} (CURRENT DATE - ONLY)`,
                );

                const { data: dateFolderFiles, error: dateFolderError } =
                  await supabase.storage
                    .from(bucketName_files)
                    .list(fullDateFolderPath);

                if (!dateFolderError && dateFolderFiles) {
                  const filesOnly = dateFolderFiles.filter((f) =>
                    f.name.includes("."),
                  );
                  filesOnly.forEach((file) => {
                    // Only add files from current date folder
                    fileMap.set(file.name, {
                      ...file,
                      actualFolderPath: fullDateFolderPath,
                    });
                  });
                  console.log(
                    `✅ Fetched ${filesOnly.length} files from: ${fullDateFolderPath} (CURRENT DATE)`,
                  );
                } else if (dateFolderError) {
                  console.warn(
                    `⚠️ Error listing date folder ${fullDateFolderPath}:`,
                    dateFolderError.message,
                  );
                }
              }
            } else if (dateFoldersError) {
              console.warn(
                `⚠️ Error listing date folders in ${patientFolderPath}:`,
                dateFoldersError.message,
              );
            }
          }
        } else if (listError) {
          console.warn(
            `⚠️ Error listing folders in base path: ${listError.message}`,
          );
        }
      }

      // Convert Map to Array and sort by name
      const combinedFiles = Array.from(fileMap.values()).sort((a, b) =>
        a.name.localeCompare(b.name),
      );
      console.log(`✅ Total unique files found: ${combinedFiles.length}`);

      setCurrentFolderFiles(combinedFiles);
      // Store the patient whose reports modal is opening (used by handleProcessReport)
      const viewingPatient =
        allSchedules.find((s) => s.id === patientId) ||
        (patientId ? { id: patientId } : null);
      setCurrentViewingPatient(viewingPatient);
      // Always open the modal, even if there's an error
      setModalIsOpen(true);
    } catch (error) {
      console.error("❌ Error in handleViewReports:", error);
      setCurrentFolderFiles([]);
      setModalIsOpen(true);
    }
  };

  openReportsRef.current = (row) => {
    if (!row?.id) return;
    handleViewReports(row.reports || buildReportsFolder(row), row.id);
  };

  // Open FormsModal - data fetching is handled internally by the component
  const handleViewForms = (patientId, patientData = null) => {
    console.log("🔍 Opening forms modal for patient:", patientId);
    setSelectedPatientIdForForms(patientId);
    setSelectedPatientDataForForms(patientData);
    setIsFormsModalOpen(true);
  };

  // Handle generate from forms - opens the prompt editor modal
  // Uses shared utilities from formProcessingUtils.js
  const handleGenerateFromForms = async (qualifyingFormLinks, patientData) => {
    console.log(
      "🔍 SchedulesTable: handleGenerateFromForms called with",
      qualifyingFormLinks?.length,
      "qualifying forms",
    );

    if (!patientData) {
      toast({
        title: "Error",
        description: "Patient data not available",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const processingData = await prepareFormProcessingData(patientData);
      setCurrentPatientType(processingData.patientType);
      setPromptEditorInitialData(processingData);
      setSelectedPatientDataForProcessing(patientData);
      setIsPromptEditorOpen(true);
    } catch (error) {
      console.error("❌ SchedulesTable: Error preparing form prompt:", error);
      toast({
        title: "Error",
        description: `Failed to prepare form prompt: ${error.message}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Callback from ProcessFormPromptEditor - navigates to PatientPage
  const handleFormProcessingRun = ({
    fullPrompt,
    useStoredFullText,
    forceProcessForms,
  }) => {
    if (!selectedPatientDataForProcessing) {
      toast({
        title: "Error",
        description: "No patient data available",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const visitSeg = getPatientPageVisitPathSegment(
      selectedPatientDataForProcessing,
    );
    if (!visitSeg) return;
    const search = `?date=${selectedDate}&openProcessForms=1`;
    const formsRoutePrefix = canAccessPage(effectiveUserRole, 'clinicPage') ? 'clinic' : 'visit';
    const url = `${window.location.origin}${basename || ""}/${formsRoutePrefix}/${visitSeg}${search}`;
    try {
      localStorage.setItem(
        "notesai_processForms_payload",
        JSON.stringify({
          prefillPrompt: fullPrompt,
          prefillUseStoredFullText: useStoredFullText ?? false,
          prefillForceProcessForms: forceProcessForms ?? false,
        }),
      );
      window.open(url, "_blank", "noopener,noreferrer");
      toast({
        title: "Opened in new tab",
        description: "AI chat opened in a new tab. This page stays here.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      console.error("Failed to open process forms in new tab:", e);
      navigate(`/${formsRoutePrefix}/${visitSeg}?date=${selectedDate}`, {
        state: {
          openProcessForms: true,
          prefillPrompt: fullPrompt,
          prefillUseStoredFullText: useStoredFullText,
          prefillForceProcessForms: forceProcessForms,
        },
      });
    }
  };

  // "Use Doc as Template" from SchedulesTable: open PatientPage in a new tab with the
  // openDocAsTemplate flag so PatientPage can navigate to edit-document with the correct
  // file path (same relay pattern as handleFormProcessingRun → openProcessForms).
  const handleDocAsTemplateFromSchedulesTable = async ({
    editablePrompt,
    useStoredFullText,
    forceProcessForms,
  }) => {
    // Patient page route uses schedule row UUID (`id`), not EHR_ID
    const visitSeg = getPatientPageVisitPathSegment(
      selectedPatientDataForProcessing,
    );
    const row = selectedPatientDataForProcessing;
    const fp = row?.filePath ?? row?.file_path;
    if (!visitSeg) return;
    let documentRowId = null;
    try {
      const supabase = await getSupabase();
      const simple = await deriveSimpleScheduleMedADocsPath(row);
      const long = await deriveLongScheduleMedADocsPath(row);
      const lookupPaths = [];
      if (simple) lookupPaths.push(simple);
      if (fp && fp !== simple) lookupPaths.push(fp);
      if (long && long !== simple && long !== fp) lookupPaths.push(long);
      if (lookupPaths.length) {
        const res = await resolveDocumentsTableIdByPathFirstMatch(
          supabase,
          lookupPaths,
        );
        documentRowId = res.id;
      }
    } catch (_) {
      /* non-fatal */
    }
    try {
      localStorage.setItem(
        "notesai_docAsTemplate_payload",
        JSON.stringify({
          editablePrompt,
          useStoredFullText,
          forceProcessForms,
          documentRowId,
        }),
      );
    } catch (e) {
      // ignore storage errors
    }
    const search = `?date=${selectedDate}&openDocAsTemplate=1`;
    const templateRoutePrefix = canAccessPage(effectiveUserRole, 'clinicPage') ? 'clinic' : 'visit';
    const url = `${window.location.origin}${basename || ""}/${templateRoutePrefix}/${visitSeg}${search}`;
    setIsPromptEditorOpen(false);
    window.open(url, "_blank", "noopener,noreferrer");
    toast({
      title: "Opened in new tab",
      description: "The document will open and apply changes automatically.",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  // Open MessagingModal - only available in AdminPage
  const handleViewMessages = (
    patientId,
    patientData = null,
    unreadCount = 0,
  ) => {
    console.log(
      "📱 Opening messaging modal for patient:",
      patientId,
      "unread:",
      unreadCount,
    );
    setSelectedPatientForMessaging(patientData);
    setSelectedPatientUnreadCount(unreadCount);
    setIsMessagingModalOpen(true);
  };

  // Refresh current folder files (used when file processing completes)
  const refreshCurrentFolderFiles = async () => {
    if (!currentFolderPath) return;

    const supabase = await getSupabase();
    console.log("🔄 Refreshing folder files for:", currentFolderPath);

    try {
      const { data, error } = await supabase.storage
        .from(bucketName_files)
        .list(currentFolderPath);

      if (error) {
        console.error("Error refreshing folder files:", error);
      } else {
        const filesOnly = data.filter((item) => item.name.includes("."));
        setCurrentFolderFiles(filesOnly);
        console.log(`✅ Refreshed ${filesOnly.length} files`);
      }
    } catch (error) {
      console.error("Error refreshing folder files:", error);
    }
  };

  // Handle folder navigation
  const handleGoUpOneLevel = async () => {
    const supabase = await getSupabase();
    const parentFolderPath = currentFolderPath
      .split("/")
      .slice(0, -1)
      .join("/");
    setPreviousFolderPath(currentFolderPath);
    setCurrentFolderPath(parentFolderPath);
    const { data, error } = await supabase.storage
      .from(bucketName_files)
      .list(parentFolderPath);
    if (error) {
      console.error("Error fetching folders from Supabase:", error);
    } else {
      setCurrentFolderFiles(data);
    }
  };

  const handleGoDownOneLevel = async (folder) => {
    const supabase = await getSupabase();
    const newPath = `${currentFolderPath}/${folder}`;
    setPreviousFolderPath(currentFolderPath);
    setCurrentFolderPath(newPath);
    const { data, error } = await supabase.storage
      .from(bucketName_files)
      .list(newPath);
    if (error) {
      console.error("Error fetching folders from Supabase:", error);
    } else {
      setCurrentFolderFiles(data);
    }
  };

  const handleViewSavedResults = async (patient) => {
    try {
      const supabase = await getSupabase();
      // A row merged in from a linked clinic lives in ITS schema (`_schema`),
      // so read it through that schema's client — the viewer's table has no
      // such id and the unqualified `.single()` used to 406, leaving the T
      // button dead for shared patients even though their audio played fine.
      const rowSchema = storageSchemaOf(patient, supabaseSchema);
      // Fetch only the transcripts column for this patient from Supabase
      const { data, error } = await dbForRow(supabase, patient, supabaseSchema)
        .from(tableName)
        .select("transcripts")
        .eq("id", patient.id)
        .single();

      console.log(
        "Fetched transcript for patient:",
        patient.id,
        data?.transcripts,
      );

      if (error) throw error;

      const visitDate = patient.date_ || selectedDate;
      const visitMoreAudios = filterAudiosForVisitDate(
        patient.more_audios || [],
        visitDate,
        rowSchema,
      );

      let rawTranscripts = [];
      if (data && data.transcripts) {
        rawTranscripts = Array.isArray(data.transcripts)
          ? data.transcripts
          : [data.transcripts];
      }

      const patientTranscriptions = filterTranscriptsForVisitDate(
        rawTranscripts,
        visitDate,
        visitMoreAudios,
        rowSchema,
      );

      setSavedTranscriptions(patientTranscriptions);
      setSelectedPatientForSavedResults(patient);
      setIsViewSavedResultsModalOpen(true);
    } catch (error) {
      console.error("Error fetching saved transcript from Supabase:", error);
      toast({
        title: "Error",
        description: "Failed to fetch saved transcript from database.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getPlainTranscriptTextForAi = (transcription) => {
    if (!transcription?.transcript) return "";
    const tr = transcription.transcript;
    if (Array.isArray(tr)) {
      return tr
        .map((line) =>
          typeof line === "object"
            ? line?.transcript || line?.text || ""
            : String(line),
        )
        .join(" ")
        .trim();
    }
    if (typeof tr === "object") {
      return (tr.transcript || tr.text || "").trim();
    }
    return String(tr).trim();
  };

  const findAiRecapEntry = (transcriptId, allTranscripts) => {
    if (!Array.isArray(allTranscripts) || !transcriptId) return null;
    return (
      allTranscripts.find(
        (r) => r.is_ai_recap && r.linked_transcript_id === transcriptId,
      ) || null
    );
  };

  const handleGenerateAiSummary = async (transcription) => {
    const text = getPlainTranscriptTextForAi(transcription);
    if (!text) {
      toast({
        title: "No transcript text",
        description: "Add or save a transcript before generating an AI summary.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (!selectedPatientForSavedResults?.id) return;

    setGeneratingAiSummaryId(transcription.id);
    try {
      const supabase = await getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      const baseUrl = getBackendURL();
      const res = await fetch(`${baseUrl}/api/generate-ai-recap/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Schema": supabaseSchema || "public",
        },
        body: JSON.stringify({
          transcript: text,
          patientId: String(selectedPatientForSavedResults.id),
          sessionId: `transcript_${transcription.id}`,
          schema: supabaseSchema || "public",
          userJwtToken: session?.access_token || null,
        }),
      });
      const json = await res.json();
      if (!res.ok || json.error) {
        throw new Error(json.error || "Failed to generate AI summary");
      }
      const recap = json.recap;
      const { data: patientData, error: fetchErr } = await dbForRow(supabase, selectedPatientForSavedResults, supabaseSchema)
        .from(tableName)
        .select("transcripts")
        .eq("id", selectedPatientForSavedResults.id)
        .single();
      if (fetchErr) throw fetchErr;

      const list = Array.isArray(patientData?.transcripts)
        ? [...patientData.transcripts]
        : [];
      const existingRecap = list.find(
        (tx) =>
          tx.is_ai_recap && tx.linked_transcript_id === transcription.id,
      );

      if (existingRecap) {
        const updated = list.map((tx) =>
          tx.id === existingRecap.id
            ? {
                ...tx,
                summary: recap.summary || "",
                key_takeaways: recap.key_takeaways || [],
                action_items: recap.action_items || [],
                created_at: new Date().toISOString(),
              }
            : tx,
        );
        const { error: upErr } = await dbForRow(supabase, selectedPatientForSavedResults, supabaseSchema)
          .from(tableName)
          .update({ transcripts: updated })
          .eq("id", selectedPatientForSavedResults.id);
        if (upErr) throw upErr;
      } else {
        const recapEntry = {
          id: crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random()}`,
          is_ai_recap: true,
          linked_transcript_id: transcription.id,
          patient_id: String(selectedPatientForSavedResults.id),
          summary: recap.summary || "",
          key_takeaways: recap.key_takeaways || [],
          action_items: recap.action_items || [],
          created_at: new Date().toISOString(),
        };
        const { error: upErr } = await dbForRow(supabase, selectedPatientForSavedResults, supabaseSchema)
          .from(tableName)
          .update({ transcripts: [...list, recapEntry] })
          .eq("id", selectedPatientForSavedResults.id);
        if (upErr) throw upErr;
      }

      await handleViewSavedResults(selectedPatientForSavedResults);
      toast({
        title: "AI summary saved",
        description: "Summary is stored with this transcript.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      console.error("handleGenerateAiSummary:", e);
      toast({
        title: "AI summary failed",
        description: e.message || "Failed to generate summary.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setGeneratingAiSummaryId(null);
    }
  };

  // Show the T button only if the patient has a transcript in the database.
  // One batched query per schema (rows merged from other clinics carry `_schema`)
  // instead of a one-row `.single()` per schedule: a row that lives in another
  // schema does not exist in the viewer's table, and every such lookup used to
  // fail with a 406 on each page load.
  useEffect(() => {
    const fetchTranscriptFlags = async () => {
      try {
        const supabase = await getSupabase();
        const bySchema = new Map();
        for (const schedule of schedules) {
          if (!schedule?.id) continue;
          const key = foreignSchemaOf(schedule, supabaseSchema) || "";
          if (!bySchema.has(key)) bySchema.set(key, []);
          bySchema.get(key).push(schedule);
        }
        const flags = {};
        for (const [foreign, rows] of bySchema) {
          const client = foreign ? supabase.schema(foreign) : supabase;
          const { data, error } = await client
            .from(tableName)
            .select("id, transcripts")
            .in("id", rows.map((r) => r.id));
          if (error) throw error;
          const transcriptsById = new Map((data || []).map((d) => [d.id, d.transcripts]));
          for (const schedule of rows) {
            const raw = transcriptsById.get(schedule.id);
            const rawList = raw ? (Array.isArray(raw) ? raw : [raw]) : [];
            const visitTranscripts = filterTranscriptsForVisitDate(
              rawList,
              schedule.date_ || selectedDate,
              schedule.more_audios || [],
              storageSchemaOf(schedule, supabaseSchema),
            );
            flags[schedule.id] = visitTranscripts.some((t) => !t.is_ai_recap);
          }
        }
        setSavedTranscriptionsCount(flags);
      } catch (error) {
        console.error("Error fetching transcript flags from Supabase:", error);
      }
    };
    if (schedules && schedules.length > 0) {
      fetchTranscriptFlags();
    }
  }, [schedules]);

  // Add this effect to fetch initial live_transcription_enabled values
  const processedSchedulesRef = useRef(new Set());

  useEffect(() => {
    if (schedules?.length > 0) {
      // Check if we've already processed these schedules
      const scheduleIds = schedules.map((s) => s.id).join(",");
      if (processedSchedulesRef.current.has(scheduleIds)) {
        return; // Already processed, skip
      }

      const updatedSchedules = schedules.map((schedule) => {
        const liveTranscriptionEnabled = getLiveTranscriptionSetting(
          schedule.id,
        );
        return {
          ...schedule,
          live_transcription_enabled: liveTranscriptionEnabled,
        };
      });

      // Rows from getSchedules already carry these values; re-setting an
      // identical list would only run every [schedules] effect again.
      const changed = updatedSchedules.some(
        (s, i) =>
          s.live_transcription_enabled !==
          schedules[i].live_transcription_enabled,
      );
      if (changed) {
        setLocalSchedules(updatedSchedules);
        if (setSchedules) setSchedules(updatedSchedules);
      }

      // Mark as processed
      processedSchedulesRef.current.add(scheduleIds);
    }
  }, [schedules]);

  // Listen for global live transcription setting changes
  useEffect(() => {
    const handleGlobalLiveTranscriptionChange = (event) => {
      console.log(
        "🌍 SchedulesTable: Global live transcription setting changed:",
        event.detail,
      );
      const { enabled } = event.detail;

      // Update all schedules to reflect the global setting
      if (schedules?.length > 0) {
        const updatedSchedules = schedules.map((schedule) => ({
          ...schedule,
          live_transcription_enabled: enabled,
        }));

        setLocalSchedules(updatedSchedules);
        if (setSchedules) setSchedules(updatedSchedules);

        // Clear the processed cache to allow re-processing
        processedSchedulesRef.current.clear();
      }
    };

    window.addEventListener(
      "globalLiveTranscriptionChanged",
      handleGlobalLiveTranscriptionChange,
    );

    return () => {
      window.removeEventListener(
        "globalLiveTranscriptionChanged",
        handleGlobalLiveTranscriptionChange,
      );
    };
  }, [schedules, setSchedules]);

  const handleDeleteTranscription = async (transcriptionId) => {
    try {
      const supabase = await getSupabase();

      // Get current transcriptions for the patient
      const { data: currentData, error: fetchError } = await dbForRow(supabase, selectedPatientForSavedResults, supabaseSchema)
        .from(tableName)
        .select("transcripts")
        .eq("id", selectedPatientForSavedResults.id)
        .single();

      if (fetchError) throw fetchError;

      if (currentData && currentData.transcripts) {
        // Filter out the transcription to delete
        let updatedTranscripts = [];
        if (Array.isArray(currentData.transcripts)) {
          updatedTranscripts = currentData.transcripts.filter(
            (t) => t.id !== transcriptionId,
          );
        } else {
          // Single transcription object - if it's the one to delete, set to empty array
          updatedTranscripts =
            currentData.transcripts.id === transcriptionId
              ? []
              : [currentData.transcripts];
        }

        // Update the database with the filtered array
        const { error: updateError } = await dbForRow(supabase, selectedPatientForSavedResults, supabaseSchema)
          .from(tableName)
          .update({ transcripts: updatedTranscripts })
          .eq("id", selectedPatientForSavedResults.id);

        if (updateError) throw updateError;

        // Refresh the list
        await handleViewSavedResults(selectedPatientForSavedResults);

        toast({
          title: "Success",
          description: "Transcription deleted successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error deleting transcription from Supabase:", error);
      toast({
        title: "Error",
        description: "Failed to delete transcription from database.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleExportToPDF = async (transcription) => {
    try {
      const { jsPDF } = await import("jspdf");

      // Create new PDF document
      const doc = new jsPDF();

      // Add title
      doc.setFontSize(18);
      doc.text("Transcription Report", 20, 20);

      // Add patient info
      doc.setFontSize(12);
      doc.text(
        `Patient: ${
          selectedPatientForSavedResults?.[prompts[0][0]] || "Unknown"
        }`,
        20,
        35,
      );
      doc.text(
        `Date: ${new Date(transcription.created_at).toLocaleDateString()}`,
        20,
        45,
      );
      doc.text(
        `Time: ${new Date(transcription.created_at).toLocaleTimeString()}`,
        20,
        55,
      );

      if (transcription.audio_duration) {
        const minutes = Math.floor(transcription.audio_duration / 60);
        const seconds = Math.round(transcription.audio_duration % 60);
        doc.text(
          `Audio Duration: ${minutes}:${seconds.toString().padStart(2, "0")}`,
          20,
          65,
        );
      }

      let yPosition = 80;

      // Add transcript
      if (transcription.transcript) {
        doc.setFontSize(14);
        doc.text("Transcript:", 20, yPosition);
        yPosition += 10;

        doc.setFontSize(10);
        const transcriptText = Array.isArray(transcription.transcript)
          ? transcription.transcript
              .map((line) =>
                typeof line === "string"
                  ? line
                  : line?.transcript || line?.text || "",
              )
              .filter(Boolean)
              .join("\n")
          : typeof transcription.transcript === "object" &&
              transcription.transcript !== null
            ? transcription.transcript.transcript ||
              transcription.transcript.text ||
              ""
            : String(transcription.transcript ?? "");

        const transcriptLines = doc.splitTextToSize(transcriptText, 170);
        doc.text(transcriptLines, 20, yPosition);
        yPosition += transcriptLines.length * 5 + 10;
      }

      // Add facts
      if (
        transcription.facts &&
        Array.isArray(transcription.facts) &&
        transcription.facts.length > 0
      ) {
        doc.setFontSize(14);
        doc.text("Extracted Facts:", 20, yPosition);
        yPosition += 10;

        doc.setFontSize(10);
        transcription.facts.forEach((fact, index) => {
          const factText = `${index + 1}. ${fact.text} (${fact.group})`;
          const factLines = doc.splitTextToSize(factText, 170);
          doc.text(factLines, 20, yPosition);
          yPosition += factLines.length * 5 + 5;

          // Add new page if needed
          if (yPosition > 250) {
            doc.addPage();
            yPosition = 20;
          }
        });
        yPosition += 10;
      }

      // Add SOAP notes
      if (transcription.soap_notes) {
        doc.setFontSize(14);
        doc.text("SOAP Notes:", 20, yPosition);
        yPosition += 10;

        doc.setFontSize(10);
        if (transcription.soap_notes.transcript_only) {
          doc.text("Transcript-Based:", 20, yPosition);
          yPosition += 5;
          const soapLines = doc.splitTextToSize(
            transcription.soap_notes.transcript_only,
            170,
          );
          doc.text(soapLines, 20, yPosition);
          yPosition += soapLines.length * 5 + 10;
        }

        if (transcription.soap_notes.facts_only) {
          doc.text("Facts-Based:", 20, yPosition);
          yPosition += 5;
          const soapLines = doc.splitTextToSize(
            transcription.soap_notes.facts_only,
            170,
          );
          doc.text(soapLines, 20, yPosition);
          yPosition += soapLines.length * 5 + 10;
        }
      }

      // Save the PDF
      const fileName = `transcription_${
        selectedPatientForSavedResults?.[prompts[0][0]] || "patient"
      }_${new Date(transcription.created_at).toISOString().split("T")[0]}.pdf`;
      doc.save(fileName);

      toast({
        title: "Success",
        description: "PDF exported successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      toast({
        title: "Error",
        description: "Failed to export PDF.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Comment handlers
  const handleCommentChange = (transcriptionId, value) => {
    setSavedTranscriptions((prev) =>
      prev.map((transcription) =>
        transcription.id === transcriptionId
          ? { ...transcription, newComment: value }
          : transcription,
      ),
    );
  };

  const handleAddComment = async (transcriptionId) => {
    try {
      const transcription = savedTranscriptions.find(
        (t) => t.id === transcriptionId,
      );
      if (
        !transcription ||
        !transcription.newComment ||
        transcription.newComment.trim() === ""
      ) {
        return;
      }

      const newComment = {
        text: transcription.newComment.trim(),
        timestamp: new Date().toISOString(),
      };

      const supabase = await getSupabase();

      // Get current transcriptions for the patient
      const { data: currentData, error: fetchError } = await dbForRow(supabase, selectedPatientForSavedResults, supabaseSchema)
        .from(tableName)
        .select("transcripts")
        .eq("id", selectedPatientForSavedResults.id)
        .single();

      if (fetchError) throw fetchError;

      if (currentData && currentData.transcripts) {
        // Update the specific transcription with the new comment
        let updatedTranscripts = [];
        if (Array.isArray(currentData.transcripts)) {
          updatedTranscripts = currentData.transcripts.map((t) =>
            t.id === transcriptionId
              ? {
                  ...t,
                  comments: [...(t.comments || []), newComment],
                  newComment: "", // Clear the input
                }
              : t,
          );
        } else {
          // Single transcription object
          if (currentData.transcripts.id === transcriptionId) {
            updatedTranscripts = [
              {
                ...currentData.transcripts,
                comments: [
                  ...(currentData.transcripts.comments || []),
                  newComment,
                ],
                newComment: "",
              },
            ];
          } else {
            updatedTranscripts = [currentData.transcripts];
          }
        }

        // Update the database
        const { error: updateError } = await dbForRow(supabase, selectedPatientForSavedResults, supabaseSchema)
          .from(tableName)
          .update({ transcripts: updatedTranscripts })
          .eq("id", selectedPatientForSavedResults.id);

        if (updateError) throw updateError;

        // Update local state
        setSavedTranscriptions((prev) =>
          prev.map((t) =>
            t.id === transcriptionId
              ? {
                  ...t,
                  comments: [...(t.comments || []), newComment],
                  newComment: "",
                }
              : t,
          ),
        );

        toast({
          title: "Success",
          description: "Comment added successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      toast({
        title: "Error",
        description: "Failed to add comment.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeleteComment = async (transcriptionId, commentIndex) => {
    try {
      const supabase = await getSupabase();

      // Get current transcriptions for the patient
      const { data: currentData, error: fetchError } = await dbForRow(supabase, selectedPatientForSavedResults, supabaseSchema)
        .from(tableName)
        .select("transcripts")
        .eq("id", selectedPatientForSavedResults.id)
        .single();

      if (fetchError) throw fetchError;

      if (currentData && currentData.transcripts) {
        // Remove the specific comment
        let updatedTranscripts = [];
        if (Array.isArray(currentData.transcripts)) {
          updatedTranscripts = currentData.transcripts.map((t) =>
            t.id === transcriptionId
              ? {
                  ...t,
                  comments: t.comments
                    ? t.comments.filter((_, index) => index !== commentIndex)
                    : [],
                }
              : t,
          );
        } else {
          // Single transcription object
          if (currentData.transcripts.id === transcriptionId) {
            updatedTranscripts = [
              {
                ...currentData.transcripts,
                comments: currentData.transcripts.comments
                  ? currentData.transcripts.comments.filter(
                      (_, index) => index !== commentIndex,
                    )
                  : [],
              },
            ];
          } else {
            updatedTranscripts = [currentData.transcripts];
          }
        }

        // Update the database
        const { error: updateError } = await dbForRow(supabase, selectedPatientForSavedResults, supabaseSchema)
          .from(tableName)
          .update({ transcripts: updatedTranscripts })
          .eq("id", selectedPatientForSavedResults.id);

        if (updateError) throw updateError;

        // Update local state
        setSavedTranscriptions((prev) =>
          prev.map((t) =>
            t.id === transcriptionId
              ? {
                  ...t,
                  comments: t.comments
                    ? t.comments.filter((_, index) => index !== commentIndex)
                    : [],
                }
              : t,
          ),
        );

        toast({
          title: "Success",
          description: "Comment deleted successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast({
        title: "Error",
        description: "Failed to delete comment.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Fetch data on mount and when dependencies change
  useEffect(() => {
    const fetchData = async () => {
      const gen = ++fetchGenRef.current;
      // No-shows only label the row buttons, so they load alongside the rows
      // instead of in front of them; getSchedules awaits them at that point,
      // which is also where a failure surfaces.
      const nsPromise = fetchNoShows(selectedDate);
      nsPromise.then((nsData) => {
        if (gen === fetchGenRef.current) setNoShows(nsData);
      }, () => {});
      await getSchedules(nsPromise, gen);
      // A superseded run leaves the confirmation badges to the run that
      // replaced it, which loads them for its own date and providers.
      if (gen !== fetchGenRef.current) return;
      await loadConfirmationAutoStatus();
    };
    fetchData();
  }, [selectedDate, refreshData, providersKey]);

  // Listen for patientDataRefresh event to refresh schedules
  useEffect(() => {
    const handlePatientDataRefresh = () => {
      console.log(
        "🔄 SchedulesTable: Received patientDataRefresh event, refreshing schedules",
      );
      setRefreshData((prev) => !prev);

      // Force a delay to ensure the data is refreshed before calculating credits
      setTimeout(() => {
        console.log(
          "🔄 SchedulesTable: Triggering credit calculation after data refresh",
        );
        // Dispatch a custom event to trigger credit calculation
        window.dispatchEvent(new CustomEvent("schedulesDataRefreshed"));
      }, 1000);
    };

    window.addEventListener("patientDataRefresh", handlePatientDataRefresh);

    return () => {
      window.removeEventListener(
        "patientDataRefresh",
        handlePatientDataRefresh,
      );
    };
  }, []);

  // Also trigger credit calculation when schedules data actually changes
  useEffect(() => {
    if (schedules && schedules.length > 0) {
      console.log(
        "🔄 SchedulesTable: Schedules data changed, triggering credit calculation",
      );
      // Dispatch event to trigger credit calculation in parent component
      window.dispatchEvent(new CustomEvent("schedulesDataRefreshed"));
    }
  }, [schedules]);

  // Update pagination when the *visible* rows or items per page change —
  // paging off the unfiltered list would leave empty trailing pages whenever
  // a filter is on.
  useEffect(() => {
    setTotalPages(Math.max(1, Math.ceil(visibleSchedules.length / itemsPerPage)));
    setCurrentPage(1);
  }, [visibleSchedules, itemsPerPage]);

  // Storage listings behind the Reports indicator, shared by every check
  // during one fetch (local edits hand the effect below new rows) and dropped
  // when the next fetch starts, so a refetch, e.g. after a report is filed,
  // lists afresh.
  const reportListingsRef = useRef({ gen: 0, listings: new Map() });

  // Check which schedules have files in their reports folder
  useEffect(() => {
    let cancelled = false;
    const checkFilesForSchedules = async () => {
      if (!schedules || schedules.length === 0) {
        console.log(
          "🔍 checkFilesForSchedules: No schedules, clearing files set",
        );
        setSchedulesWithFiles(new Set());
        return;
      }

      console.log(
        `🔍 checkFilesForSchedules: Checking ${schedules.length} schedules for files`,
      );
      const schedulesWithFilesSet = new Set();
      const fileReferenceColumn = "reports"; // Assuming this is the column name
      // Fall back to the derived reports folder when the `reports` column is
      // blank — that column is only auto-set by the fax match/assign flow, so
      // files uploaded another way would otherwise never be detected.
      const folderOf = (schedule) =>
        schedule[fileReferenceColumn] || buildReportsFolder(schedule);

      // All rows in one pass (utils/reportsFolderFiles.js): each schema folder
      // is listed once instead of once per row, with the same per-row rules.
      if (reportListingsRef.current.gen !== fetchGenRef.current) {
        reportListingsRef.current = {
          gen: fetchGenRef.current,
          listings: new Map(),
        };
      }
      let foldersWithReports = new Set();
      try {
        const supabase = await getSupabase();
        foldersWithReports = await scheduleFoldersWithFiles(
          supabase,
          bucketName_files,
          schedules.map(folderOf),
          { listings: reportListingsRef.current.listings },
        );
      } catch (error) {
        console.error("❌ Error checking files existence:", error);
      }
      // Newer rows started their own check; these may no longer be shown.
      if (cancelled) return;

      schedules.forEach((schedule) => {
        const folderPath = folderOf(schedule);
        if (folderPath) {
          const hasFiles = foldersWithReports.has(folderPath);
          console.log(
            `🔍 checkFilesForSchedules: Schedule ${schedule.id} (${schedule[prompts[0][0]] || "unknown"}) has files: ${hasFiles} (folder: ${folderPath})`,
          );
          if (hasFiles) {
            schedulesWithFilesSet.add(schedule.id);
          }
        } else {
          console.log(
            `🔍 checkFilesForSchedules: Schedule ${schedule.id} has no folder path`,
          );
        }
      });

      console.log(
        `✅ checkFilesForSchedules: Found ${schedulesWithFilesSet.size} schedules with files:`,
        Array.from(schedulesWithFilesSet),
      );
      setSchedulesWithFiles(schedulesWithFilesSet);
      setFilesCheckComplete(true);
    };

    setFilesCheckComplete(false);
    checkFilesForSchedules();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedules]);

  // Add this effect to update selectedDate when the URL changes
  useEffect(() => {
    const urlDate = new URLSearchParams(location.search).get("date");
    if (urlDate && urlDate !== selectedDate) {
      setSelectedDate(urlDate);
      // Clear selection state when date changes
      setSelectedRows({});
    }
    if (!urlDate && selectedDate !== getDefaultDateString()) {
      setSelectedDate(getDefaultDateString());
      // Clear selection state when date changes
      setSelectedRows({});
    }
  }, [location.search]);

  // Manual Confirm-Status override. Writes through the backend (service role)
  // rather than direct Supabase, so the audit fields are stamped in one place.
  const handleSetConfirmationStatus = async (rowId, status) => {
    const priorRow = schedules.find((s) => s.id === rowId);
    // A row merged in from a linked provider clinic lives in ITS schema, not
    // the viewer's. Addressing it with `supabaseSchema` would run the UPDATE
    // against the wrong schema's "notes-c", match zero rows, and still answer
    // success — the same silent no-op utils/rowSchema.js documents. Home rows
    // give foreignSchemaOf() null and fall back to supabaseSchema unchanged.
    const rowSchema =
      foreignSchemaOf(priorRow, supabaseSchema) || supabaseSchema || "public";
    // Capture every field the optimistic update below is about to touch, so
    // a failed request can restore the row byte-identical to how it started
    // — not just the status, or an SMS-sourced row would silently lose its
    // "Replied M/D" footer (confirmation_source stranded at "manual").
    const previous = {
      confirmation_status: priorRow?.confirmation_status,
      confirmation_source: priorRow?.confirmation_source,
      confirmation_updated_at: priorRow?.confirmation_updated_at,
      confirmation_updated_by: priorRow?.confirmation_updated_by,
    };
    // Optimistic: the badge is the whole point of the click.
    const optimistic = {
      confirmation_status: status,
      confirmation_source: "manual",
      confirmation_updated_at: new Date().toISOString(),
      confirmation_updated_by: currentUserEmail || null,
    };
    setLocalSchedules((prev) =>
      prev.map((s) => (s.id === rowId ? { ...s, ...optimistic } : s)),
    );
    if (onRowPatched) onRowPatched(rowId, optimistic);
    try {
      const response = await fetch(
        `${getBackendURL()}/twilio/set-confirmation-status/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            schema: rowSchema,
            patient_id: rowId,
            status,
            // Real editor when the session gave us one; "" otherwise (the
            // backend maps "" to None and leaves the audit column alone).
            user_email: currentUserEmail || "",
          }),
        },
      );
      const payload = await response.json();
      if (!response.ok || payload.status !== "success") {
        throw new Error(payload.error || `HTTP ${response.status}`);
      }
      loadConfirmationAutoStatus();
      return true;
    } catch (error) {
      // Roll every touched field back rather than leaving the table lying
      // about the DB (or stranding confirmation_source at "manual").
      setLocalSchedules((prev) =>
        prev.map((s) => (s.id === rowId ? { ...s, ...previous } : s)),
      );
      if (onRowPatched) onRowPatched(rowId, previous);
      toast({
        title: "Could not update confirmation status",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
  };
  setConfirmationStatusRef.current = handleSetConfirmationStatus;

  // JSX rendering
  return (
    <Box
      className="schedules-container"
      width="100%"
      overflowX="auto"
      p={2}
    >
      {isLoading && (
        <Center>
          <Spinner size="xl" />
        </Center>
      )}
      {fetchError && (
        <Text color="red.500" mb={2}>
          {fetchError}
        </Text>
      )}
      {!isLoading && !fetchError && (
        <>
          {/* Compact toolbar — owner name on the left, Start recording + view
              controls on the right. Replaces the two stacked header bars +
              centered "Start recording" Flex that were eating ~140px above
              the table. */}
          <div className="w-full flex flex-wrap items-center justify-between gap-2 mb-2">
            <div className="flex items-baseline gap-2 min-w-0">
              <span className="text-sm font-bold text-gray-900 truncate">
                Schedule for {currentUserName || "User"}
              </span>
              <span className="text-[11px] text-gray-500 tabular-nums">
                {selectedDate}
              </span>
              {filtersActive && (
                <span className="text-[11px] text-gray-500 tabular-nums">
                  · showing {visibleSchedules.length} of {schedules.length}
                </span>
              )}
            </div>
            <HStack spacing={2} justify="flex-end" flexWrap="wrap">
              {/* View filters. These narrow the rendered rows only — the
                  provider *selection* (which schemas get fetched at all) still
                  lives in ProvidersDropdown up on the page. */}
              {availableTypes.length > 1 && (
                <Menu closeOnSelect={false}>
                  <MenuButton
                    as={Button}
                    size="sm"
                    colorScheme="purple"
                    variant={typeFilter.length ? "solid" : "outline"}
                    title="Filter the table by appointment type"
                  >
                    Type
                    {typeFilter.length ? ` (${typeFilter.length})` : ""}
                  </MenuButton>
                  <MenuList minW="230px" maxH="320px" overflowY="auto" zIndex={20}>
                    <MenuOptionGroup
                      type="checkbox"
                      value={typeFilter}
                      onChange={(v) =>
                        setTypeFilter(Array.isArray(v) ? v : [v])
                      }
                    >
                      {availableTypes.map((t) => (
                        <MenuItemOption key={t} value={t} fontSize="sm">
                          {TYPE_LABELS[t] ? `${t} — ${TYPE_LABELS[t]}` : t}
                          <Text as="span" color="gray.500" ml={1}>
                            ({typeCounts[t] || 0})
                          </Text>
                        </MenuItemOption>
                      ))}
                    </MenuOptionGroup>
                    {typeFilter.length > 0 && (
                      <>
                        <MenuDivider />
                        <Box px={3} pb={1}>
                          <Button
                            size="xs"
                            variant="link"
                            onClick={() => setTypeFilter([])}
                          >
                            Clear type filter
                          </Button>
                        </Box>
                      </>
                    )}
                  </MenuList>
                </Menu>
              )}
              {availableSchemas.length > 1 && (
                <Menu closeOnSelect={false}>
                  <MenuButton
                    as={Button}
                    size="sm"
                    colorScheme="teal"
                    variant={schemaFilter.length ? "solid" : "outline"}
                    title="Show only rows from certain providers"
                  >
                    Provider
                    {schemaFilter.length ? ` (${schemaFilter.length})` : ""}
                  </MenuButton>
                  <MenuList minW="230px" maxH="320px" overflowY="auto" zIndex={20}>
                    <MenuOptionGroup
                      type="checkbox"
                      value={schemaFilter}
                      onChange={(v) =>
                        setSchemaFilter(Array.isArray(v) ? v : [v])
                      }
                    >
                      {availableSchemas.map((s) => (
                        <MenuItemOption key={s} value={s} fontSize="sm">
                          {s === homeSchema ? `${s} (home)` : s}
                          <Text as="span" color="gray.500" ml={1}>
                            ({schemaCounts[s] || 0})
                          </Text>
                        </MenuItemOption>
                      ))}
                    </MenuOptionGroup>
                    {schemaFilter.length > 0 && (
                      <>
                        <MenuDivider />
                        <Box px={3} pb={1}>
                          <Button
                            size="xs"
                            variant="link"
                            onClick={() => setSchemaFilter([])}
                          >
                            Clear provider filter
                          </Button>
                        </Box>
                      </>
                    )}
                  </MenuList>
                </Menu>
              )}
              {!patientView && currentUserId && (
                <Button
                  leftIcon={<FontAwesomeIcon icon={faMicrophone} />}
                  colorScheme="red"
                  size="sm"
                  isDisabled={Object.keys(selectedRows).length !== 1}
                  title={
                    Object.keys(selectedRows).length !== 1
                      ? "Select exactly one row to start recording"
                      : "Start recording for selected " +
                        getLabel(effectiveUserRole, 'entity').toLowerCase()
                  }
                  onClick={() => {
                    const ids = Object.keys(selectedRows);
                    if (ids.length !== 1) return;
                    const row = schedules.find((s) => s.id === ids[0]);
                    if (row) handleStartRecordingForRow(row);
                  }}
                >
                  Start recording
                </Button>
              )}
            <Button
              onClick={() =>
                setCurrentView(
                  currentView === "default" ? "compact" : "default",
                )
              }
              colorScheme="blue"
              size="sm"
            >
              {currentView === "default"
                ? "Switch to Compact View"
                : "Switch to Detailed View"}
            </Button>
            {!patientView && (
            <Button
              onClick={() => {
                console.log(
                  `🗑️ Remove ${getLabel(effectiveUserRole, 'entity')} button clicked!`,
                );
                console.log(`🗑️ Selected rows:`, selectedRows);
                console.log(
                  `🗑️ Selected rows count:`,
                  Object.keys(selectedRows).length,
                );
                handleRemovePatient();
              }}
              colorScheme="red"
              size="sm"
              isDisabled={isRemoveRowDisabled}
              title={
                isRemoveRowDisabled &&
                Object.keys(selectedRows).length > 0 &&
                isInGroup(effectiveUserRole, 'tenantSchema')
                  ? "You cannot remove these rows with viewer or editor access. Ask the owner or an admin share to remove them."
                  : Object.keys(selectedRows).length === 0
                    ? "Select one or more rows to remove"
                    : undefined
              }
            >
              {Object.keys(selectedRows).length > 1
                ? `Remove ${Object.keys(selectedRows).length} selected`
                : `Remove ${getLabel(effectiveUserRole, 'entity')}`}
            </Button>
            )}
            <CSVLink
              data={prepareCSVData()}
              filename={`schedules-${selectedDate}.csv`}
            >
              <Button colorScheme="green" size="sm">Export as CSV</Button>
            </CSVLink>
            <Button
              colorScheme="blue"
              size="sm"
              onClick={() => setImportModalOpen(true)}
              title="Import appointments from a CSV or Excel file"
            >
              Import CSV / Excel
            </Button>
            {!patientView && currentUserId && (
              <Box position="relative" display="inline-block">
                <Button
                  leftIcon={<FontAwesomeIcon icon={faShare} />}
                  colorScheme="teal"
                  variant="outline"
                  size="sm"
                  onClick={() => setShareAllModalOpen(true)}
                  title="Share all your rows (past and future) with other users"
                >
                  Share all rows
                </Button>
                {shareAllBadgeCount > 0 && (
                  <Box
                    position="absolute"
                    top="-6px"
                    right="-6px"
                    bg="teal.500"
                    color="white"
                    borderRadius="full"
                    w="18px"
                    h="18px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize="10px"
                    fontWeight="bold"
                    pointerEvents="none"
                  >
                    {shareAllBadgeCount}
                  </Box>
                )}
              </Box>
            )}
          </HStack>
          </div>
          {importModalOpen && (
            <ScheduleImportModal
              isOpen
              onClose={() => setImportModalOpen(false)}
              selectedDate={selectedDate}
              defaultType={
                (getScheduleField(effectiveUserRole, "prompts") || [])
                  .find((p) => p[0] === "type")?.[3] || ""
              }
              onImported={() => setRefreshData((prev) => !prev)}
            />
          )}
          <ResultsTable
            results={visibleSchedules.slice(
              (currentPage - 1) * itemsPerPage,
              currentPage * itemsPerPage,
            )}
            setResults={setLocalSchedules}
            // ResultsTable already knows how to chip a row that came from
            // another provider's schema, but the chip is gated on homeSchema
            // and this table never passed it — so with a provider filter now
            // available, wire it up.
            homeSchema={homeSchema}
            prompts={prompts}
            hiddenColumns={hiddenColumns}
            userRole={effectiveUserRole}
            selectedRows={selectedRows}
            handleCheckboxChange={handleCheckboxChange}
            handleViewJSONFiles={handleViewReports}
            handleViewFolderFiles={handleViewReports}
            currentFolderPath={currentFolderPath}
            previousFolderPath={previousFolderPath}
            handleGoUpOneLevel={handleGoUpOneLevel}
            handleGoDownOneLevel={handleGoDownOneLevel}
            supabaseUrl={supabaseUrl}
            bucketName_files={bucketName_files}
            modalIsOpen={modalIsOpen}
            setModalIsOpen={setModalIsOpen}
            currentFolderFiles={currentFolderFiles}
            refreshCurrentFolderFiles={refreshCurrentFolderFiles}
            showCheckboxes={showCheckboxes}
            showFiles={true}
            // The schedules view renders THIS prop, not `results` (which only
            // the search variant reads) — so the type/provider filters have to
            // be applied here or they do nothing at all. Left unpaginated, the
            // way this prop has always been passed.
            schedules={visibleSchedules}
            schedulesDeps={{
              buttonTexts,
              defaultButtonTexts,
              dups,
              noShows,
              isInTimeRange,
              setButtonTexts,
              handleRowClick,
              handleRecordAudio: patientView ? undefined : handleRecordAudio,
              isAdminPage,
              showMsgColumn:
                !patientView && isAdminPage && !isInGroup(effectiveUserRole, 'tenantSchema'),
              // Confirm-Status shows on Schedules AND Admin — the only gate is
              // that patients don't see their own confirmation controls.
              showConfirmColumn: !patientView,
              canEditConfirmation: hasClinicalAdminPermissions(effectiveUserRole),
              onSetConfirmationStatus: handleSetConfirmationStatus,
              confirmationAutoStatus,
              confirmationWindowDays,
              refreshUnreadMessagesTrigger,
              handleViewSavedResults,
              selectedDate,
              onShareSchedule: (schedule) => {
                // eslint-disable-next-line no-console
                console.log("[SchedulesTable DEBUG] onShareSchedule called", {
                  scheduleKeys: schedule ? Object.keys(schedule) : null,
                  user_id: schedule?.user_id,
                  id: schedule?.id,
                });
                setShareSchedule(schedule);
                setShareModalOpen(true);
              },
              canShareSchedule: (schedule) => {
                if (patientView) return false;
                const targetUserId = schedule?.user_id ?? null;
                const tenant = isInGroup(effectiveUserRole, 'tenantSchema');
                if (!currentUserId) return false;
                if (!tenant) {
                  return Boolean(targetUserId === currentUserId);
                }
                const acc = resolveTenantRowAccess({
                  viewerId: currentUserId,
                  rowUserId: targetUserId,
                  sharedUsers: schedule?.shared_users,
                  shareAllOwnerIds: tenantShareAllOwnerIds,
                  shareAllOwnerRoles: tenantShareAllOwnerRoles,
                });
                return acc.canShareRow;
              },
            }}
            activePatients={activePatients}
            activeDocuments={activeDocuments}
            activeDisplayOrder={displayOrder[currentView]}
            schedulesWithFiles={schedulesWithFiles}
            filesCheckComplete={filesCheckComplete}
            onViewForms={handleViewForms}
            onViewMessages={patientView ? undefined : handleViewMessages}
            processingFiles={processingFiles}
            onProcessReport={patientView ? undefined : handleProcessReport}
            viewerTimezone={viewerTimezone}
          ></ResultsTable>
          <Center mt={4}>
            <HStack spacing={4} justify="center" align="center">
              <Button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                isDisabled={currentPage === 1}
              >
                Previous
              </Button>
              <Text>
                Page {currentPage} of {totalPages}
              </Text>
              <Button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                isDisabled={currentPage >= totalPages}
              >
                Next
              </Button>
              <Select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(parseInt(e.target.value, 10));
                  setCurrentPage(1);
                }}
                width="auto"
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={100}>100 per page</option>
              </Select>
            </HStack>
          </Center>
        </>
      )}
      <AudioPlayerModal
        audio={audio}
        allAudios={allAudios}
        transcripts={audioTranscripts}
        scheId={scheId}
        isOpen={isAudioPlayerModalOpen}
        onClose={closeAudioPlayerModal}
      />
      <AddPatientModal
        isOpen={!patientView && isAddPatientModalOpen}
        onClose={() => {
          setIsAddPatientModalOpen(false);
          setSelectedPatient(null);
        }}
        onAddPatient={async (newPatient) => {
          const updatedSchedules = [...schedules, newPatient];
          setLocalSchedules(updatedSchedules);
          if (setSchedules) setSchedules(updatedSchedules);
          setSelectedRows({});
          setButtonTexts([
            ...buttonTexts,
            isAdminPage
              ? ""
              : isInGroup(effectiveUserRole, 'tenantSchema')
                ? `View ${getLabel(effectiveUserRole, 'entityPlural')}`
                : `See ${getLabel(effectiveUserRole, 'entity')}`,
          ]);
          setDefaultButtonTexts([
            ...defaultButtonTexts,
            isAdminPage
              ? ""
              : isInGroup(effectiveUserRole, 'tenantSchema')
                ? `View ${getLabel(effectiveUserRole, 'entityPlural')}`
                : `See ${getLabel(effectiveUserRole, 'entity')}`,
          ]);
          setTotalPages(
            Math.max(1, Math.ceil(updatedSchedules.length / itemsPerPage)),
          );
          setIsAddPatientModalOpen(false);
          setSelectedPatient(null);
          toast({
            title: "Success",
            description: "Patient added successfully.",
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        }}
        initialData={selectedPatient}
      />

      {/* Share schedule with other users (tenant: student, socialworker, other) */}
      <ShareRowModal
        isOpen={shareModalOpen}
        onClose={() => {
          setShareModalOpen(false);
          setShareSchedule(null);
        }}
        title={`Share ${shareSchedule?.[prompts[0][0]] || "visit"} with user`}
        loading={shareModalLoading}
        users={shareModalUsers}
        currentUserId={currentUserId}
        ownerId={shareSchedule?.user_id}
        alreadySharedIds={shareModalAlreadySharedIds}
        onShare={handleAddSharedUser}
        onRemove={handleRemoveSharedUser}
        removingId={shareModalRemovingId}
        sharedUserRoles={shareModalSharedUserRoles}
        onRoleChange={handleUpdateShareModalRole}
        updatingRoleId={shareModalUpdatingRoleId}
      />

      {/* Share all rows (past and future) — uses the same component as the
          per-row Share modal so the layout is identical. Each per-row Share
          click commits ONE user to the share-all list. */}
      <ShareRowModal
        isOpen={shareAllModalOpen}
        onClose={() => {
          setShareAllModalOpen(false);
          setShareAllSelectedIds(new Set());
          setShareAllRoleByUserId({});
          setShareAllSearchQuery("");
        }}
        title="Share all rows (past and future)"
        loading={shareAllLoading}
        users={shareAllUsersList}
        currentUserId={currentUserId}
        alreadySharedIds={
          new Set(shareAllRecipientEntries.map((e) => String(e.id)))
        }
        sharedUserRoles={shareAllRecipientEntries.reduce((acc, e) => {
          acc[String(e.id)] = normalizeShareRole(e.role);
          return acc;
        }, {})}
        onShare={handleShareAllSingleUser}
        onRemove={(user) => handleRevokeShareAll(user.id)}
        onRoleChange={handleUpdateShareAllRecipientRole}
        updatingRoleId={shareAllUpdatingRoleId}
        removingId={shareAllRevokingId}
        defaultShareRole={shareAllDefaultRole}
      />

      {/* Folder Viewing Modal - Removed because ResultsTable has its own modal */}

      {/* View Saved Results Modal */}
      <Modal
        isOpen={isViewSavedResultsModalOpen}
        onClose={() => setIsViewSavedResultsModalOpen(false)}
        size="6xl"
      >
        <ModalOverlay />
        <ModalContent maxH="90vh" maxW="90vw">
          <ModalHeader>
            Saved Transcriptions -{" "}
            {selectedPatientForSavedResults?.[prompts[0][0]] ||
              selectedPatientForSavedResults?.patient_name ||
              selectedPatientForSavedResults?.name}
            <Text fontSize="sm" color="gray.500" fontWeight="normal">
              (
              {savedTranscriptions.filter((t) => !t.is_ai_recap).length}{" "}
              transcription
              {savedTranscriptions.filter((t) => !t.is_ai_recap).length !== 1
                ? "s"
                : ""}
              )
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody maxH="70vh" overflowY="auto">
            {savedTranscriptions.length === 0 ? (
              <Text color="gray.500" textAlign="center" py={4}>
                No saved transcriptions found for this visit date.
              </Text>
            ) : (
              <Accordion allowMultiple>
                {savedTranscriptions
                  .filter((t) => !t.is_ai_recap)
                  .map((transcription, index) => {
                    const aiRecap = findAiRecapEntry(
                      transcription.id,
                      savedTranscriptions,
                    );
                    const canAiSummary =
                      getPlainTranscriptTextForAi(transcription).length > 0;
                    return (
                  <AccordionItem
                    key={transcription.id || index}
                    borderWidth={1}
                    borderRadius="md"
                    mb={2}
                  >
                    <AccordionButton
                      bg="gray.50"
                      _hover={{ bg: "gray.100" }}
                      p={4}
                    >
                      <Box flex="1" textAlign="left">
                        <HStack justify="space-between" mb={2}>
                          <HStack spacing={2}>
                            <Text fontWeight="bold" fontSize="lg">
                              Transcription #{index + 1}
                            </Text>
                            <Badge
                              colorScheme={
                                transcriptShowsAsLive(transcription)
                                  ? "green"
                                  : "blue"
                              }
                              fontSize="0.75em"
                            >
                              {transcriptShowsAsLive(transcription)
                                ? "Live Transcript"
                                : "Corti Transcript"}
                            </Badge>
                            {aiRecap && (
                              <Badge colorScheme="purple" fontSize="0.75em">
                                AI summary saved
                              </Badge>
                            )}
                          </HStack>
                          <Text fontSize="sm" color="gray.500">
                            {new Date(
                              transcription.created_at,
                            ).toLocaleDateString()}{" "}
                            at{" "}
                            {new Date(
                              transcription.created_at,
                            ).toLocaleTimeString()}
                          </Text>
                        </HStack>

                        <HStack spacing={4} fontSize="sm" color="gray.600">
                          <Text>
                            🎵 Audio Duration:{" "}
                            {transcription.audio_duration
                              ? `${Math.floor(
                                  transcription.audio_duration / 60,
                                )}:${(transcription.audio_duration % 60)
                                  .toString()
                                  .padStart(2, "0")}`
                              : transcription.elapsed_time
                                ? `${Math.floor(
                                    transcription.elapsed_time / 60,
                                  )}:${(transcription.elapsed_time % 60)
                                    .toString()
                                    .padStart(2, "0")}`
                                : "Duration unknown"}
                          </Text>
                          {transcription.elapsed_time &&
                            transcription.audio_duration && (
                              <Text fontSize="xs" color="gray.500">
                                ⏱️ Processed in:{" "}
                                {Math.floor(transcription.elapsed_time / 60)}:
                                {(transcription.elapsed_time % 60)
                                  .toString()
                                  .padStart(2, "0")}
                              </Text>
                            )}
                          <Text>
                            🎵 Audio:{" "}
                            {transcription.audio_file_name || "Unknown"}
                          </Text>
                          <Text>
                            📝 Source:{" "}
                            {transcription.audio_source === "url"
                              ? "Patient Audio"
                              : transcription.audio_source === "recording" ||
                                  transcription.audio_source ===
                                    "live_recording"
                                ? "Live Recording"
                                : transcription.audio_source ===
                                    "live_transcript"
                                  ? "Live Transcript"
                                  : "Uploaded File"}
                          </Text>
                          {transcription.estimated_credit_usage && (
                            <Text>
                              💳 Credits Used:{" "}
                              {transcription.estimated_credit_usage}
                            </Text>
                          )}
                        </HStack>
                      </Box>
                      <HStack spacing={2}>
                        <Button
                          size="sm"
                          colorScheme="purple"
                          variant="solid"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGenerateAiSummary(transcription);
                          }}
                          isLoading={
                            generatingAiSummaryId === transcription.id
                          }
                          loadingText="…"
                          isDisabled={!canAiSummary}
                          title={
                            aiRecap
                              ? "Regenerate AI summary (same API as audio flow)"
                              : "Create AI summary from this transcript"
                          }
                        >
                          {aiRecap ? "Regenerate AI summary" : "AI summary"}
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="blue"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExportToPDF(transcription);
                          }}
                          title="Export to PDF"
                        >
                          Export PDF
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="green"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            const lines = Array.isArray(transcription.transcript)
                              ? transcription.transcript.map((l) =>
                                  typeof l === "string" ? l : l?.transcript || l?.text || "",
                                ).filter(Boolean)
                              : [String(transcription.transcript || "")];
                            const blob = new Blob([lines.join("\n")], { type: "text/plain" });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            const patientName = (transcription.name || "patient").replace(/[^a-zA-Z0-9_-]/g, "_");
                            const dateStr = transcription.scheduled_date || transcription.date_of_service || (transcription.created_at ? transcription.created_at.split("T")[0] : "transcript");
                            a.download = `${patientName}_${dateStr}_transcript.txt`;
                            a.click();
                            URL.revokeObjectURL(url);
                          }}
                          title="Download transcript as .txt"
                        >
                          Download
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="teal"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTranscription(transcription);
                            setIsViewNotesModalOpen(true);
                          }}
                          title="View Notes"
                        >
                          View Notes
                        </Button>
                        <Button
                          size="sm"
                          colorScheme="red"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTranscription(transcription.id);
                          }}
                          title="Delete transcription"
                        >
                          ✕
                        </Button>
                        <AccordionIcon />
                      </HStack>
                    </AccordionButton>

                    <AccordionPanel bg="white" p={4}>
                      <VStack spacing={3} align="stretch">
                        {transcription.transcript && (
                          <Box>
                            <Text fontWeight="semibold" mb={2} color="blue.600">
                              📄 Transcript:
                            </Text>
                            <Box
                              maxH="200px"
                              overflowY="auto"
                              p={3}
                              bg="gray.50"
                              borderRadius="md"
                              borderWidth={1}
                            >
                              {Array.isArray(transcription.transcript) ? (
                                transcription.transcript.map((line, i) => (
                                  <Text
                                    key={i}
                                    mb={1}
                                    fontSize="sm"
                                    whiteSpace="pre-wrap"
                                    wordBreak="break-word"
                                  >
                                    {typeof line === "object"
                                      ? line.transcript || line.text || ""
                                      : line}
                                  </Text>
                                ))
                              ) : (
                                <Text
                                  fontSize="sm"
                                  whiteSpace="pre-wrap"
                                  wordBreak="break-word"
                                >
                                  {typeof transcription.transcript === "object"
                                    ? transcription.transcript.transcript ||
                                      transcription.transcript.text ||
                                      ""
                                    : transcription.transcript}
                                </Text>
                              )}
                            </Box>
                          </Box>
                        )}

                        {aiRecap && (
                          <Box
                            borderWidth={1}
                            borderColor="purple.200"
                            borderRadius="md"
                            p={3}
                            bg="purple.50"
                          >
                            <Text
                              fontWeight="bold"
                              mb={2}
                              color="purple.700"
                            >
                              AI Summary
                            </Text>
                            {aiRecap.summary ? (
                              <Box mb={3}>
                                <Text
                                  fontSize="xs"
                                  fontWeight="semibold"
                                  color="gray.600"
                                  mb={1}
                                >
                                  Summary
                                </Text>
                                <Text fontSize="sm" color="gray.800">
                                  {aiRecap.summary}
                                </Text>
                              </Box>
                            ) : null}
                            {aiRecap.key_takeaways?.length > 0 && (
                              <Box mb={3}>
                                <Text
                                  fontSize="xs"
                                  fontWeight="semibold"
                                  color="gray.600"
                                  mb={1}
                                >
                                  Key takeaways
                                </Text>
                                <UnorderedList spacing={1} pl={2}>
                                  {aiRecap.key_takeaways.map((item, ki) => (
                                    <ListItem key={ki} fontSize="sm">
                                      {formatAiRecapBullet(item)}
                                    </ListItem>
                                  ))}
                                </UnorderedList>
                              </Box>
                            )}
                            {aiRecap.action_items?.length > 0 && (
                              <Box>
                                <Text
                                  fontSize="xs"
                                  fontWeight="semibold"
                                  color="gray.600"
                                  mb={1}
                                >
                                  Action items
                                </Text>
                                <UnorderedList spacing={1} pl={2}>
                                  {aiRecap.action_items.map((item, ai) => (
                                    <ListItem key={ai} fontSize="sm">
                                      {formatAiRecapBullet(item)}
                                    </ListItem>
                                  ))}
                                </UnorderedList>
                              </Box>
                            )}
                          </Box>
                        )}

                        {transcription.facts && (
                          <Box>
                            <Text
                              fontWeight="semibold"
                              mb={2}
                              color="green.600"
                            >
                              🔍 Extracted Facts:
                            </Text>
                            <Box
                              maxH="200px"
                              overflowY="auto"
                              p={3}
                              bg="gray.50"
                              borderRadius="md"
                              borderWidth={1}
                            >
                              {Array.isArray(transcription.facts) ? (
                                transcription.facts.map((fact, i) => (
                                  <Box
                                    key={i}
                                    mb={2}
                                    p={2}
                                    bg="white"
                                    borderRadius="md"
                                  >
                                    <Text fontSize="sm">{fact.text}</Text>
                                    <Text fontSize="xs" color="gray.500">
                                      Category: {fact.group}
                                    </Text>
                                  </Box>
                                ))
                              ) : (
                                <Text fontSize="sm">{transcription.facts}</Text>
                              )}
                            </Box>
                          </Box>
                        )}

                        {transcription.soap_notes && (
                          <Box>
                            <Text
                              fontWeight="semibold"
                              mb={2}
                              color="purple.600"
                            >
                              📋 SOAP Notes:
                            </Text>
                            <Box
                              maxH="200px"
                              overflowY="auto"
                              p={3}
                              bg="gray.50"
                              borderRadius="md"
                              borderWidth={1}
                            >
                              {transcription.soap_notes.transcript_only && (
                                <Box mb={3}>
                                  <Text
                                    fontWeight="medium"
                                    fontSize="sm"
                                    mb={1}
                                    color="blue.600"
                                  >
                                    Transcript-Based:
                                  </Text>
                                  <Text fontSize="sm" whiteSpace="pre-wrap">
                                    {transcription.soap_notes.transcript_only}
                                  </Text>
                                </Box>
                              )}
                              {transcription.soap_notes.facts_only && (
                                <Box>
                                  <Text
                                    fontWeight="medium"
                                    fontSize="sm"
                                    mb={1}
                                    color="green.600"
                                  >
                                    Facts-Based:
                                  </Text>
                                  <Text fontSize="sm" whiteSpace="pre-wrap">
                                    {transcription.soap_notes.facts_only}
                                  </Text>
                                </Box>
                              )}
                            </Box>
                          </Box>
                        )}

                        {/* Comments Section */}
                        <Box>
                          <Text fontWeight="semibold" mb={2} color="orange.600">
                            💬 Comments:
                          </Text>
                          <VStack spacing={2} align="stretch">
                            {/* Display existing comments */}
                            {transcription.comments &&
                              transcription.comments.length > 0 && (
                                <Box
                                  maxH="150px"
                                  overflowY="auto"
                                  p={3}
                                  bg="orange.50"
                                  borderRadius="md"
                                  borderWidth={1}
                                >
                                  {transcription.comments.map(
                                    (comment, commentIndex) => (
                                      <Box
                                        key={commentIndex}
                                        mb={2}
                                        p={2}
                                        bg="white"
                                        borderRadius="md"
                                        borderWidth={1}
                                      >
                                        <HStack justify="space-between" mb={1}>
                                          <Text fontSize="xs" color="gray.500">
                                            {new Date(
                                              comment.timestamp,
                                            ).toLocaleString()}
                                          </Text>
                                          <Button
                                            size="xs"
                                            colorScheme="red"
                                            variant="ghost"
                                            onClick={() =>
                                              handleDeleteComment(
                                                transcription.id,
                                                commentIndex,
                                              )
                                            }
                                          >
                                            ✕
                                          </Button>
                                        </HStack>
                                        <Text fontSize="sm">
                                          {comment.text}
                                        </Text>
                                      </Box>
                                    ),
                                  )}
                                </Box>
                              )}

                            {/* Add new comment */}
                            <HStack spacing={2}>
                              <Input
                                placeholder="Add a comment..."
                                value={transcription.newComment || ""}
                                onChange={(e) =>
                                  handleCommentChange(
                                    transcription.id,
                                    e.target.value,
                                  )
                                }
                                size="sm"
                                flex={1}
                              />
                              <Button
                                size="sm"
                                colorScheme="orange"
                                onClick={() =>
                                  handleAddComment(transcription.id)
                                }
                                isDisabled={
                                  !transcription.newComment ||
                                  transcription.newComment.trim() === ""
                                }
                              >
                                Add
                              </Button>
                            </HStack>
                          </VStack>
                        </Box>
                      </VStack>
                    </AccordionPanel>
                  </AccordionItem>
                );
                })}
              </Accordion>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setIsViewSavedResultsModalOpen(false)}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* View Notes Modal */}
      <Modal
        isOpen={isViewNotesModalOpen}
        onClose={() => setIsViewNotesModalOpen(false)}
        size="6xl"
      >
        <ModalOverlay />
        <ModalContent maxH="90vh" bg="white">
          <ModalHeader
            bg="white"
            borderBottom="2px solid"
            borderColor="gray.300"
            textAlign="center"
          >
            <Heading size="xl" fontWeight="bold" color="black">
              Medical Report Transcription
            </Heading>
          </ModalHeader>
          <ModalCloseButton color="gray.600" />
          <ModalBody
            pb={6}
            overflowY="scroll"
            bg="white"
            maxH="60vh"
            css={{
              "&::-webkit-scrollbar": {
                width: "12px",
                display: "block !important",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "6px",
                border: "2px solid #f1f1f1",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#555",
              },
              "&::-webkit-scrollbar:vertical": {
                display: "block !important",
              },
              "&::-webkit-scrollbar:horizontal": {
                display: "block !important",
              },
            }}
          >
            {selectedTranscription && (
              <Box>
                {/* Patient Information Header - PDF style */}
                <Box textAlign="center" py={6}>
                  <VStack spacing={2} align="center">
                    <Text fontSize="lg" fontWeight="bold" color="black">
                      Patient:{" "}
                      {selectedTranscription.name ||
                        selectedPatientForSavedResults?.[prompts[0][0]] ||
                        "Unknown"}
                    </Text>
                    <Text fontSize="lg" fontWeight="bold" color="black">
                      Date:{" "}
                      {selectedTranscription.created_at
                        ? new Date(
                            selectedTranscription.created_at,
                          ).toLocaleDateString()
                        : "Unknown"}
                    </Text>
                    <Text fontSize="lg" fontWeight="bold" color="black">
                      Duration:{" "}
                      {selectedTranscription.audio_duration
                        ? `${Math.floor(
                            selectedTranscription.audio_duration / 60,
                          )}:${String(
                            Math.floor(
                              selectedTranscription.audio_duration % 60,
                            ),
                          ).padStart(2, "0")}`
                        : "0:00"}
                    </Text>
                  </VStack>
                </Box>

                {/* Transcript Section - PDF style */}
                {selectedTranscription.transcript && (
                  <Box mb={8}>
                    <Heading size="lg" mb={4} color="black" fontWeight="bold">
                      Transcript:
                    </Heading>
                    <Box
                      p={0}
                      bg="white"
                      fontSize="md"
                      lineHeight="1.8"
                      color="black"
                      fontFamily="serif"
                    >
                      {Array.isArray(selectedTranscription.transcript) ? (
                        selectedTranscription.transcript.map((line, index) => (
                          <Text
                            key={index}
                            mb={3}
                            lineHeight="1.8"
                            fontFamily="serif"
                          >
                            {typeof line === "object"
                              ? line.transcript || line.text || ""
                              : line}
                          </Text>
                        ))
                      ) : (
                        <Text
                          lineHeight="1.8"
                          fontFamily="serif"
                          whiteSpace="pre-wrap"
                        >
                          {typeof selectedTranscription.transcript === "object"
                            ? selectedTranscription.transcript.transcript ||
                              selectedTranscription.transcript.text ||
                              ""
                            : selectedTranscription.transcript}
                        </Text>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Extracted Facts Section - PDF style */}
                {selectedTranscription.facts && (
                  <Box mb={8}>
                    <Heading size="lg" mb={4} color="black" fontWeight="bold">
                      Extracted Facts:
                    </Heading>
                    <VStack align="stretch" spacing={3}>
                      {Array.isArray(selectedTranscription.facts) ? (
                        selectedTranscription.facts.map((fact, index) => (
                          <Box
                            key={index}
                            p={4}
                            bg="white"
                            border="1px solid"
                            borderColor="gray.400"
                            fontSize="md"
                            color="black"
                            fontFamily="serif"
                          >
                            <Text fontFamily="serif">{fact.text}</Text>
                          </Box>
                        ))
                      ) : (
                        <Box
                          p={4}
                          bg="white"
                          border="1px solid"
                          borderColor="gray.400"
                        >
                          <Text fontFamily="serif">
                            {JSON.stringify(selectedTranscription.facts)}
                          </Text>
                        </Box>
                      )}
                    </VStack>
                  </Box>
                )}

                {/* SOAP Notes Section - PDF style */}
                {selectedTranscription.soap_notes && (
                  <Box mb={8}>
                    <Heading size="lg" mb={4} color="black" fontWeight="bold">
                      SOAP Notes:
                    </Heading>

                    {/* Debug: Log SOAP notes structure */}
                    {console.log(
                      "SOAP Notes Debug:",
                      selectedTranscription.soap_notes,
                    )}

                    {/* Handle different SOAP notes structures */}
                    {typeof selectedTranscription.soap_notes === "string" ? (
                      <Box
                        p={0}
                        bg="white"
                        fontSize="md"
                        lineHeight="1.8"
                        color="black"
                        fontFamily="serif"
                      >
                        <Text
                          whiteSpace="pre-wrap"
                          lineHeight="1.8"
                          fontFamily="serif"
                        >
                          {selectedTranscription.soap_notes}
                        </Text>
                      </Box>
                    ) : selectedTranscription.soap_notes.transcript_only ||
                      selectedTranscription.soap_notes.facts_only ? (
                      <>
                        {selectedTranscription.soap_notes.transcript_only && (
                          <Box mb={6}>
                            <Text
                              fontSize="md"
                              fontWeight="bold"
                              mb={3}
                              color="black"
                            >
                              Transcript-Based SOAP Notes:
                            </Text>
                            <Box
                              p={0}
                              bg="white"
                              fontSize="md"
                              lineHeight="1.8"
                              color="black"
                              fontFamily="serif"
                            >
                              <Text
                                whiteSpace="pre-wrap"
                                lineHeight="1.8"
                                fontFamily="serif"
                              >
                                {
                                  selectedTranscription.soap_notes
                                    .transcript_only
                                }
                              </Text>
                            </Box>
                          </Box>
                        )}

                        {selectedTranscription.soap_notes.facts_only && (
                          <Box>
                            <Text
                              fontSize="md"
                              fontWeight="bold"
                              mb={3}
                              color="black"
                            >
                              Facts-Based SOAP Notes:
                            </Text>
                            <Box
                              p={0}
                              bg="white"
                              fontSize="md"
                              lineHeight="1.8"
                              color="black"
                              fontFamily="serif"
                            >
                              <Text
                                whiteSpace="pre-wrap"
                                lineHeight="1.8"
                                fontFamily="serif"
                              >
                                {selectedTranscription.soap_notes.facts_only}
                              </Text>
                            </Box>
                          </Box>
                        )}
                      </>
                    ) : (
                      <Box
                        p={4}
                        bg="gray.50"
                        border="1px solid"
                        borderColor="gray.300"
                        borderRadius="md"
                      >
                        <Text color="gray.600" fontStyle="italic">
                          SOAP notes data structure:{" "}
                          {JSON.stringify(
                            selectedTranscription.soap_notes,
                            null,
                            2,
                          )}
                        </Text>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </ModalBody>
          <ModalFooter bg="white" borderTop="1px solid" borderColor="gray.300">
            <Button
              colorScheme="blue"
              onClick={() => setIsViewNotesModalOpen(false)}
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Forms Modal - Using shared FormsModal component */}
      <FormsModal
        isOpen={isFormsModalOpen}
        onClose={() => {
          setIsFormsModalOpen(false);
          setSelectedPatientDataForForms(null);
        }}
        patientId={selectedPatientIdForForms}
        patientData={selectedPatientDataForForms}
        readOnly={patientView}
        onProcessForms={patientView ? undefined : (qualifyingFormLinks) => {
          console.log(
            "🔍 SchedulesTable: Processing qualifying forms from modal:",
            qualifyingFormLinks,
          );
          // Find the patient data from schedules
          const patientData =
            schedules.find((s) => s.id === selectedPatientIdForForms) ||
            allSchedules.find((s) => s.id === selectedPatientIdForForms);
          if (patientData) {
            handleGenerateFromForms(qualifyingFormLinks, patientData);
          } else {
            // Fallback: fetch patient data if not found in schedules
            console.log(
              "⚠️ SchedulesTable: Patient not found in schedules, fetching from database",
            );
            getSupabase().then((supabase) => {
              supabase
                .from(tableName)
                .select("*")
                .eq("id", selectedPatientIdForForms)
                .single()
                .then(({ data, error }) => {
                  if (error || !data) {
                    toast({
                      title: "Error",
                      description: "Could not fetch patient data",
                      status: "error",
                      duration: 3000,
                      isClosable: true,
                    });
                    return;
                  }
                  handleGenerateFromForms(qualifyingFormLinks, data);
                });
            });
          }
        }}
      />

      {/* Process form Prompt Editor Modal (shared component) */}
      <ProcessFormPromptEditor
        isOpen={isPromptEditorOpen}
        onClose={() => setIsPromptEditorOpen(false)}
        onRun={handleFormProcessingRun}
        onDocAsTemplate={
          isInGroup(effectiveUserRole, "clinical")
            ? handleDocAsTemplateFromSchedulesTable
            : undefined
        }
        patientType={currentPatientType}
        initialData={promptEditorInitialData}
        toast={toast}
      />

      {/* Process Report Prompt Editor Modal (shared component) */}
      {isReportPromptEditorOpen && (
        <ProcessReportPromptEditor
          isOpen={true}
          onClose={() => setIsReportPromptEditorOpen(false)}
          onRun={handleReportProcessingRun}
          reportType={currentReportType}
          initialData={reportPromptEditorInitialData}
          toast={toast}
        />
      )}

      {/* Patient Messaging Modal - Only shown in AdminPage */}
      {isAdminPage && (
        <PatientMessagingModal
          isOpen={isMessagingModalOpen}
          onClose={() => {
            setIsMessagingModalOpen(false);
            setSelectedPatientForMessaging(null);
            setSelectedPatientUnreadCount(0);
            // Trigger refresh of unread messages count in ResultsTable after a short delay
            // to ensure database has time to mark messages as reviewed
            setTimeout(() => {
              setRefreshUnreadMessagesTrigger((prev) => prev + 1);
            }, 500);
          }}
          patientData={selectedPatientForMessaging}
          patientId={selectedPatientForMessaging?.id}
          smsCache={smsCache}
          unreadCount={selectedPatientUnreadCount}
          appointmentDate={selectedPatientForMessaging?.date_ || selectedDate}
        />
      )}
    </Box>
  );
};

export default SchedulesTable;
